import { Button, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import html2pdf from "html2pdf.js";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import noData from "../Assets/no-data-image.svg";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";

// your existing mobile components (unchanged, original props)
import CashMethod from "./CashMethod";
import CustomerDialog from "./CustomerDialog";
import EditItemDetails from "./EditItemDetails";
import HomeBillDialog from "./HomeBillDialog";
import QrCodeDialog from "./QrCodeDialog";
import SingleCardDetails from "./SingleCardDetails";

// OPTIONAL thermal printing: shared Epson helper (a function, not a dialog).
// CHECK: the argument order below matches the DESKTOP eposPrint. The mobile
// sale invoice imports a different eposPrint ("../TopTen/eposPrint") with another
// argument order, so point this import at the desktop-style file.
import { printReceipt } from "./eposPrint";

/* ------------------------------------------------------------------ */
/*  Line icons                                                         */
/* ------------------------------------------------------------------ */
const ICONS = {
    list: (
        <>
            <path d="M8 6h13M8 12h13M8 18h13" />
            <path d="M3 6h.01M3 12h.01M3 18h.01" />
        </>
    ),
    edit: (
        <>
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </>
    ),
    userPlus: (
        <>
            <circle cx="9" cy="8" r="4" />
            <path d="M2 21a7 7 0 0 1 14 0" />
            <path d="M19 8v6M16 11h6" />
        </>
    ),
    user: (
        <>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
        </>
    ),
    trash: (
        <>
            <path d="M3 6h18" />
            <path d="M8 6V4h8v2" />
            <path d="M6 6l1 14h10l1-14" />
            <path d="M10 11v6M14 11v6" />
        </>
    ),
    wallet: (
        <>
            <path d="M3 7a2 2 0 0 1 2-2h13v4" />
            <path d="M3 7v11a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1H5a2 2 0 0 1-2-2z" />
            <circle cx="16.5" cy="14.5" r="1" />
        </>
    ),
    qr: (
        <>
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20h1" />
        </>
    ),
    save: (
        <>
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <path d="M7 3v5h8V3M7 21v-7h10v7" />
        </>
    ),
    printer: (
        <>
            <path d="M6 9V3h12v6" />
            <rect x="3" y="9" width="18" height="8" rx="2" />
            <path d="M7 14h10v7H7z" />
        </>
    ),
    close: <path d="M18 6L6 18M6 6l12 12" />,
    chevron: <path d="M9 6l6 6-6 6" />,
    receipt: (
        <>
            <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2z" />
            <path d="M9 8h6M9 12h6" />
        </>
    ),
    refresh: (
        <>
            <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
            <path d="M21 3v5h-5" />
        </>
    ),
    barcode: <path d="M4 5v14M7 5v14M11 5v14M14 5v14M18 5v14M21 5v14" />,
    idcard: (
        <>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="9" cy="11" r="2" />
            <path d="M6 16a3 3 0 0 1 6 0M14 10h4M14 14h4" />
        </>
    ),
    check: <path d="M5 13l4 4L19 7" />,
};

const Ico = ({ name, size = 16, className }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {ICONS[name]}
    </svg>
);

// bottom sheet shell shared by every dialog on this screen
const BottomSheet = ({ open, title, sub, onClose, className = "", children }) => {
    if (!open) return null;
    return (
        <div className="bcs-sheet-wrap" onClick={onClose}>
            <div className={`bcs-sheet ${className}`} onClick={(e) => e.stopPropagation()}>
                <div className="bcs-grab" />
                <div className="bcs-sheet-head">
                    <span className="bcs-sheet-title">{title}</span>
                    <button className="bcs-sheet-x" onClick={onClose} aria-label="Close">
                        <Ico name="close" size={14} />
                    </button>
                </div>
                {sub ? <p className="bcs-sheet-sub">{sub}</p> : null}
                {children}
            </div>
        </div>
    );
};

const EMPTY_CUSTOMER_FORM = {
    mobile1: "",
    city: "",
    name: "",
    mobile2: "",
    phone: "",
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    state: "ANDHRA PRADESH",
    stateCode: "37",
    gstin: "",
    panNo: "",
    idProof: "",
    dob: "",
    anniversary: "",
    locationType: "LOCAL",
};

// reads the first key that exists (API rows come back with inconsistent casing)
const pick = (row, ...keys) => {
    for (const k of keys) {
        if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
    }
    return "";
};

// product names go into an HTML string for the PDF
const esc = (s) =>
    String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]);

const SaleReturnScanner = () => {
    const qrRef = useRef(null);
    const tagNoRef = useRef(null); // design code input (inside its dialog)
    const smInputRef = useRef(null); // sm code input (inside its dialog)
    const lastItemRef = useRef(null);
    const searchRef = useRef(null);
    const mobileRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [userArea, setUserArea] = useState();
    const [userName, setUserName] = useState();
    const [singleImage, setSingleImage] = useState();
    const [gstNo, setGstNo] = useState("");
    const [gstValue, setGstValue] = useState();
    const [userPhone, setUserPhone] = useState("");
    const [ins, setIns] = useState("");
    const [ins1, setIns1] = useState("");
    const [ins2, setIns2] = useState("");
    const [ins3, setIns3] = useState("");
    const [ins4, setIns4] = useState("");
    const [ins5, setIns5] = useState("");
    const [ins6, setIns6] = useState("");
    const [address, setAddress] = useState("");
    const [address1, setAddress1] = useState("");
    const [city, setCity] = useState("");

    const [returnNo, setReturnNo] = useState(0); // next free return number
    const [invNo, setInvNo] = useState(0); // set only while an existing return bill is being edited
    const [billDate, setBillDate] = useState();

    // entry dialogs
    // design code + SM code entry (inline on the screen)
    const [entryStatus, setEntryStatus] = useState(null); // design code message { type: ok | err | warn, text }
    const [entryBusy, setEntryBusy] = useState(false);
    const [smStatus, setSmStatus] = useState(null); // SM code message
    const [smBusy, setSmBusy] = useState(false);
    const [designInput, setDesignInput] = useState("");
    const [smDraft, setSmDraft] = useState("");
    const [smCodeData, setSmCodeData] = useState([]); // the applied sm code

    const [barCodeData, setBarCodeData] = useState([]);
    const [qtyData, setQtyData] = useState([]);
    const [gstData, setGstData] = useState([]);

    const [totalQty, setTotalQty] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0); // MRP total
    const [totalDisAmt, setTotalDisAmt] = useState(0); // item discount
    const [totalGrossAmt, setTotalGrossAmt] = useState(0);
    const [totalNetAmt, setTotalNetAmt] = useState(0);
    const [disPer, setDisPer] = useState(0); // bill discount % of a loaded bill
    const [totalDiscountAmt, setTotalDiscountAmt] = useState(0); // bill discount amount of a loaded bill
    const [billDiscAmt, setBillDiscAmt] = useState(0); // bill discount actually applied
    const [totalBasicAmount, setTotalBasicAmount] = useState(0);
    const [totalGstAmt, setTotalGstAmt] = useState(0);
    const [totalCGSTAmt, setTotalCGSTAmt] = useState(0);
    const [totalSGSTAmt, setTotalSGSTAmt] = useState(0);
    const [totalGstData, setTotalGstData] = useState([]);

    const [cashOpen, setCashOpen] = useState(false);
    const [cashValue, setCashValue] = useState("CASH");
    const [cashAmt, setCashAmt] = useState(0);
    const [cardAmt, setCardAmt] = useState(0);
    const [upiAmt, setUpiAmt] = useState(0);
    const [onlineAmt, setOnlineAmt] = useState(0);
    const [creditAmt, setCreditAmt] = useState(0);
    const [cardAcc, setCardAcc] = useState("");
    const [upiAcc, setUpiAcc] = useState("");
    const [onlineAcc, setOnlineAcc] = useState("");
    const [switchCredit, setSwitchCredit] = useState("Cash");
    const [creditCashValue, setCreditCashValue] = useState("CREDIT");
    const [creditCashAmt, setCreditCashAmt] = useState(0);
    const [creditCardAmt, setCreditCardAmt] = useState(0);
    const [creditUpiAmt, setCreditUpiAmt] = useState(0);
    const [creditOnlineAmt, setCreditOnlineAmt] = useState(0);

    const [base64Images, setBase64Images] = useState();
    const [base64QR, setBase64QR] = useState("");
    const [qrCodeOpen, setQrCodeOpen] = useState(false);

    const [homeDetailsOpen, setHomeDetailsOpen] = useState(false);
    const [productData, setProductData] = useState([]);
    const [qty, setQty] = useState(0);
    const [amount, setAmount] = useState(0);
    const [disCount, setDisCount] = useState(0);
    const [rate, setRate] = useState(0);
    const [disAmt, setDisAmt] = useState(0);
    const [productName, setProductName] = useState("");
    const [designCode, setDesignCode] = useState("");
    const [pTypeName, setPTypeName] = useState("");
    const [brandName, setBrandName] = useState("");
    const [hsnCode, setHsnCode] = useState("");

    const [itemDetailsOpen, setItemDetailsOpen] = useState(false);
    const [itemDetailsData, setItemDetailsData] = useState({});
    const [itemDesignCode, setItemDEsignCode] = useState();

    const [messageApi, contextHolder] = message.useMessage();
    const [userDetails, setUserDetails] = useState({});
    const [prvBalance, setPrvBalance] = useState(0);
    const [comm, setComm] = useState(0);
    const [saving, setSaving] = useState(false);
    const [printSaving, setPrintSaving] = useState(false);
    const [formValues, setFormValues] = useState(EMPTY_CUSTOMER_FORM);

    const [phoneNo, setPhoneNo] = useState();
    const [custOpen, setCustOpen] = useState(false);

    // customer flows: create dialog, edit dialog, browse list
    const [custMode, setCustMode] = useState("create"); // "create" | "edit"
    const [editKey, setEditKey] = useState(""); // original mobile of the customer being edited
    const [custListOpen, setCustListOpen] = useState(false);
    const [custList, setCustList] = useState([]);
    const [custListLoading, setCustListLoading] = useState(false);
    const [custSearch, setCustSearch] = useState("");

    // tax details card (SingleCardDetails)
    const [cardOpen, setCardOpen] = useState(false);
    const [titleValue, setTitleValue] = useState("");
    const [gst, setGst] = useState(0);
    const [gstAmt, setGstAmt] = useState(0);
    const [cGstRate, setCGstRate] = useState(0);
    const [cGstAmt, setCGstAmt] = useState(0);
    const [sGstRate, setSGstRate] = useState(0);
    const [sGstAmt, setSGstAmt] = useState(0);
    const [iGstRate, setIGstRate] = useState(0);
    const [iGstAmt, setIGstAmt] = useState(0);
    const [basicAmt, setBasicAmt] = useState(0);
    const [netAmt, setNetAmt] = useState(0);
    const [smCodeView, setSmCodeView] = useState("");

    // bills sheet
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetTab, setSheetTab] = useState("RETURN"); // RETURN | SALE
    const [sheetSearch, setSheetSearch] = useState("");
    const [sheetRows, setSheetRows] = useState([]);
    const [sheetLoading, setSheetLoading] = useState(false);
    const [sheetFrom, setSheetFrom] = useState(dayjs().subtract(6, "day").format("YYYY-MM-DD"));
    const [sheetTo, setSheetTo] = useState(dayjs().format("YYYY-MM-DD"));

    const [printOpen, setPrintOpen] = useState(false);

    const tenantName = localStorage.getItem("tenantName");
    const loginName = localStorage.getItem("loginName");
    const upiId = localStorage.getItem("upiId");
    const adminType = localStorage.getItem("adminType");
    const canEdit = adminType === "ADMIN" || (adminType === "OPERATOR" && !invNo);

    const H = { headers: { tenantName } };
    const JSON_H = { headers: { "Content-Type": "application/json", tenantName } };
    const getTable = (table, where) =>
        axios.get(
            `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=${table}&where=${where}`,
            H,
        );

    const totalAmt = totalNetAmt - cardAmt - upiAmt - onlineAmt - creditAmt;
    const invPaidAmt = totalNetAmt - creditAmt;
    const upiLink = upiId ? `upi://pay?pa=${upiId}&am=${upiAmt}&cu=INR` : "";
    const hasCustomer = Object.keys(userDetails || {}).length > 0;
    const totalBalance = Number(prvBalance) + Number(creditAmt);
    const appliedSm = smCodeData[0];

    const isValidValue = (val) =>
        val !== null &&
        val !== undefined &&
        val !== "-" &&
        String(val).trim() !== "" &&
        String(val) !== "undefined";

    const toast = (type, text) =>
        messageApi.open({
            type,
            content: <span style={{ fontSize: "15px", fontWeight: 600 }}>{text}</span>,
        });
    const toastError = (text) => toast("error", text);
    const toastOk = (text) => toast("success", text);

    const toggleDrawer = () => setOpen(false);

    /* ---------------------------- master data --------------------------- */

    const returnNoAPI = async () => {
        try {
            const response = await axios.get(
                `${CREATE_jwel}/api/ERP/GetMaxNumberInTable?tableName=RETURN_MAST&column=BILLNO`,
                H,
            );
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                const rawValue = data[0]?.Column1;
                const maxNo = Number.isFinite(Number(rawValue)) ? Number(rawValue) : 0;
                setReturnNo(maxNo + 1);
                return maxNo + 1;
            }
        } catch (error) {
            console.error("Error fetching return number:", error);
        }
    };

    const onlineAccNoAPI = async () => {
        try {
            const response = await axios.get(
                `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=ONLINEMODE_MAST`,
                H,
            );
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                const card = data.find((item) => item.PTYPE === "CARD");
                const upi = data.find((item) => item.PTYPE === "UPI");
                const online = data.find((item) => item.PTYPE === "ONLINE");
                if (card) setCardAcc(card.accno);
                if (upi) setUpiAcc(upi.accno);
                if (online) setOnlineAcc(online.accno);
            }
        } catch (error) {
            console.error("Error fetching online accounts:", error);
        }
    };

    const gstCheckAPI = async () => {
        try {
            const response = await axios.get(
                `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=PURCHASE_TYPE`,
                H,
            );
            if (Array.isArray(response.data) && response.data.length > 0) {
                setGstData(response.data);
            }
        } catch (error) {
            console.error("Error fetching GST config:", error);
        }
    };

    const commissionAPI = async () => {
        try {
            const response = await axios.get(
                `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=CONFIGURE`,
                H,
            );
            if (Array.isArray(response.data) && response.data.length > 0) {
                setComm(response.data[0]?.COMM);
            }
        } catch (error) {
            console.error("Error fetching commission:", error);
        }
    };

    const userAPI = async () => {
        try {
            const response = await axios.get(
                `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=FIRM_CONFIGURE`,
                H,
            );
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                setUserName(data[0]?.FIRMNAME);
                setUserArea(data[0]?.CITY);
                setSingleImage(data[0]?.EPASS2);
                setGstNo(data[0]?.TINNO);
                setGstValue(data[0]?.GST);
                setUserPhone(data[0]?.MOBILE);
                setIns(data[0]?.INST1);
                setIns1(data[0]?.INST2);
                setIns2(data[0]?.INST3);
                setIns3(data[0]?.INST4);
                setIns4(data[0]?.INST5);
                setIns5(data[0]?.INST6);
                setIns6(data[0]?.INST7);
                setAddress(data[0]?.ADD1);
                setAddress1(data[0]?.ADD2);
                setCity(data[0]?.CITY);
            }
        } catch (error) {
            console.error("Error fetching firm config:", error);
        }
    };

    /* ----------------------------- customers ---------------------------- */

    const customerToForm = (c) => ({
        mobile1: c?.Mobileno || "",
        city: c?.Area || "",
        name: c?.Custname || "",
        mobile2: c?.Phoneno || "",
        phone: c?.Phoneno || "",
        address1: c?.add1 || "",
        address2: c?.add2 || "",
        address3: c?.add3 || "",
        address4: c?.add4 || "",
        state: c?.STATE || "ANDHRA PRADESH",
        stateCode: c?.STATECODE || "37",
        gstin: c?.TINNO || "-",
        panNo: "",
        idProof: "",
        dob: c?.dob || "",
        anniversary: c?.ANNVERSARY || "",
        locationType: c?.STATION || "LOCAL",
    });

    const openingLedgerBalanceAPI = async (phone, name) => {
        try {
            const response = await getTable(
                "TRANS_ENTRY_DATA_CUST",
                `AGENTNAME='${phone}' AND PARTYNAME='${name}'`,
            );
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                const nama = data.reduce((acc, i) => acc + (Number(i.NAMA) || 0), 0);
                const jama = data.reduce((acc, i) => acc + (Number(i.JAMA) || 0), 0);
                setPrvBalance(nama - jama);
            } else {
                setPrvBalance(0);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // used by the bill-loading flows - loads the customer silently
    const userGetDetailsAPI = async (value) => {
        const mobileValue = isValidValue(value) ? value : phoneNo;
        if (!isValidValue(mobileValue)) return;
        try {
            const response = await getTable("CUSTOMER", `MOBILENO='${mobileValue}'`);
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                setUserDetails(data[0]);
                setFormValues(customerToForm(data[0]));
                openingLedgerBalanceAPI(data[0]?.Mobileno, data[0]?.Custname);
                setPhoneNo();
            }
        } catch (error) {
            console.error("Error fetching customer:", error);
        }
    };

    // used by the customer dialog search:
    //  - known mobile   -> loads the customer and switches the dialog to EDIT mode
    //  - unknown mobile -> stays in CREATE mode with the number pre-filled
    const userDetailsAPI = async (value) => {
        const mobileValue = isValidValue(value) ? value : phoneNo;
        if (!isValidValue(mobileValue)) return;
        try {
            const response = await getTable("CUSTOMER", `MOBILENO='${mobileValue}'`);
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                setUserDetails(data[0]);
                setFormValues(customerToForm(data[0]));
                openingLedgerBalanceAPI(data[0]?.Mobileno, data[0]?.Custname);
                setCustMode("edit");
                setEditKey(data[0]?.Mobileno || "");
                setPhoneNo();
            } else {
                setCustMode("create");
                setCustOpen(true);
                setUserDetails({});
                // use the number that was actually searched, not only the phoneNo state
                setFormValues({ ...EMPTY_CUSTOMER_FORM, mobile1: String(mobileValue) });
            }
        } catch (error) {
            console.error("Error fetching customer:", error);
        }
    };

    const clearCustomer = () => {
        setUserDetails({});
        setPhoneNo();
        setPrvBalance(0);
        setFormValues(EMPTY_CUSTOMER_FORM);
    };

    // removing the customer also drops a credit payment that depended on them
    const handleRemoveCustomer = () => {
        clearCustomer();
        if (cashValue === "CREDIT") resetPayments();
    };

    const customerBody = (value) => ({
        street: "-",
        custname: value?.name || "-",
        add1: value?.address1 || "-",
        add2: value?.address2 || "-",
        add3: value?.address3 || "-",
        area: value?.city || "-",
        phoneno: value?.phone || "-",
        mobileno: value?.mobile1 || "-",
        cardno: "",
        mstatus: "",
        occupation: "",
        education: "",
        college: "",
        discount: 0,
        dob: value?.dob || "",
        annversary: value?.anniversary || "",
        state: value?.state || "-",
        gender: "",
        district: "",
        website: "",
        category: "",
        fax: "-",
        add4: value?.address4 || "-",
        tinno: value?.gstin || "-",
        points: 0,
        statecode: value?.stateCode || "",
        station: value?.locationType || "",
        cloud_upload: false,
    });

    const insertCustomer = (value) =>
        axios.post(`${CREATE_jwel}/api/ERP/InsertCustomer`, [customerBody(value)], JSON_H);

    const deleteCustomerRow = (mobile) =>
        axios.post(
            `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=CUSTOMER&where=MOBILENO='${mobile}'`,
            {},
            H,
        );

    const closeCustDialog = () => {
        setCustOpen(false);
        setCustMode("create");
        setEditKey("");
    };

    // create OR edit, depending on custMode
    const createCustomerDetailsAPI = async (value) => {
        const wasEdit = custMode === "edit";
        try {
            if (!wasEdit) {
                const exists = await getTable("CUSTOMER", `MOBILENO='${value.mobile1}'`);
                if (Array.isArray(exists.data) && exists.data.length > 0) {
                    toastError("This mobile number already exists. Pick it from the list.");
                    return;
                }
                await insertCustomer(value);
            } else {
                const old = customerToForm(userDetails); // snapshot to restore if the insert fails
                await deleteCustomerRow(editKey);
                try {
                    await insertCustomer(value);
                } catch (err) {
                    await insertCustomer(old).catch(() => { });
                    throw err;
                }
            }
            setCustList([]); // refresh the browse list next time
            await userDetailsAPI(value?.mobile1);
            closeCustDialog();
            toastOk(wasEdit ? "Customer updated" : "Customer saved");
        } catch (error) {
            console.error("Error saving customer:", error);
            toastError("Could not save the customer. Please try again.");
        }
    };

    // top icon: always a blank CREATE form
    const openCreateCustomer = () => {
        setCustListOpen(false);
        setPhoneNo();
        setFormValues(EMPTY_CUSTOMER_FORM);
        setCustMode("create");
        setEditKey("");
        setCustOpen(true);
    };

    // pencil next to the selected customer: EDIT form
    const openEditCustomer = () => {
        if (!hasCustomer) return;
        setFormValues(customerToForm(userDetails));
        setEditKey(userDetails?.Mobileno || "");
        setCustMode("edit");
        setCustOpen(true);
    };

    // "Select customer" strip: BROWSE list
    const loadCustomers = async () => {
        setCustListLoading(true);
        try {
            const res = await axios.get(
                `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=CUSTOMER`,
                H,
            );
            setCustList(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error fetching customers:", error);
            toastError("Could not load customers.");
        } finally {
            setCustListLoading(false);
        }
    };

    const openCustList = () => {
        setCustSearch("");
        setCustListOpen(true);
        if (!custList.length) loadCustomers();
    };

    const closeCustList = () => {
        setCustListOpen(false);
        setCustSearch("");
    };

    const pickCustomer = (c) => {
        setUserDetails(c);
        setFormValues(customerToForm(c));
        openingLedgerBalanceAPI(c?.Mobileno, c?.Custname);
        closeCustList();
    };

    const custQ = custSearch.trim().toLowerCase();
    const visibleCustomers = custList
        .filter(
            (c) =>
                !custQ ||
                String(c.Custname || "").toLowerCase().includes(custQ) ||
                String(c.Mobileno || "").includes(custQ),
        )
        .slice(0, 60);

    /* ------------------------------ items ------------------------------- */

    // honorAllowFlag=true  -> scanned / loaded bills (uses item.GST when slab pricing is off)
    // honorAllowFlag=false -> items coming from the Home dialog
    // Always returns a number: an undefined GST used to become NaN in the save payload.
    const getAppliedGST = (item, honorAllowFlag = true) => {
        const gstItem = gstData.find((g) => g.PType === item.PTYPE);
        const offerAmount = item.offer ?? 0;
        const bySlab = () => {
            if (offerAmount <= gstItem.BELLOWVALUE) return gstItem.BELLOWPER;
            if (offerAmount >= gstItem.ABOVEVALUE) return gstItem.ABOVEPER;
            return 0;
        };
        let applied;
        if (honorAllowFlag) {
            applied = gstItem?.ALLOWGSTVALUE === true ? bySlab() : item?.GST;
        } else {
            applied = gstItem ? bySlab() : 0;
        }
        return Number(applied) || 0;
    };

    const qtyAPI = async (no) => {
        try {
            const response = await getTable("CURRENT_STOCK", `DESIGNCODE='${no}'`);
            const data = response.data;
            setQtyData((prev) => {
                const fresh = data
                    .filter((item) => !prev.some((p) => p.DesignCode === item.DesignCode))
                    .map((item) => ({ DesignCode: item.DesignCode, Qty: item.Qty }));
                return [...prev, ...fresh];
            });
        } catch (error) {
            console.error("Error fetching stock qty:", error);
        }
    };

    // verifies the typed sm code and applies it to the items added from now on
    // verifies the typed sm code and applies it to the items added from now on
    const smCodeAPI = async () => {
        const code = smDraft.trim();
        if (!code) {
            setSmStatus({ type: "warn", text: "Enter an SM code" });
            return;
        }
        try {
            setSmBusy(true);
            const response = await getTable("BOYMAST", `CODE='${code}'`);
            const data = response.data;
            if (!Array.isArray(data) || data.length === 0) {
                setSmStatus({ type: "err", text: `SM code ${code} was not found` });
                return;
            }
            setSmCodeData(data);
            setSmDraft("");
            setSmStatus(null);
            toastOk(`SM code ${data[0]?.CODE ?? code}${data[0]?.NAME ? " · " + data[0].NAME : ""}`);
            setTimeout(() => tagNoRef.current?.focus(), 60);
        } catch (error) {
            console.error("Error fetching sm code:", error);
            setSmStatus({ type: "err", text: "Could not check the SM code" });
        } finally {
            setSmBusy(false);
        }
    };

    const clearSmCode = () => {
        setSmCodeData([]);
        setSmDraft("");
        setSmStatus(null);
        setTimeout(() => smInputRef.current?.focus(), 60);
    };

    // returns the first stock row when the design code exists, otherwise null
    const mainAPI = async (code) => {
        const tag = code ?? designInput;
        try {
            const response = await getTable("CURRENT_STOCK", `DESIGNCODE='${tag}'`);
            const data = response.data;
            if (!Array.isArray(data) || data.length === 0) return null;
            qtyAPI(tag);
            setBarCodeData((prev) => [
                ...prev,
                ...data.map((newItem) => ({
                    ...newItem,
                    stock: 1,
                    GSTApplied: getAppliedGST(newItem, true),
                    SMCODE: smCodeData[0]?.CODE ?? "",
                })),
            ]);
            return data[0];
        } catch (error) {
            console.error("Error fetching design code:", error);
            return null;
        }
    };

    // returns true when the code exists in the product master (opens the Home dialog)
    const homeBillData = async (code) => {
        const tag = code ?? designInput;
        try {
            const response = await getTable("PRODUCT_MASTER", `SHORTKEY='${tag}'`);
            const data = response.data;
            if (!Array.isArray(data) || data.length === 0) return false;
            setProductData(data);
            setDesignCode(data[0]?.SHORTKEY);
            setHomeDetailsOpen(true);
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    // stays on the screen after an add so several items can be keyed in a row
    const handleSubmitCode = async () => {
        if (entryBusy) return;
        const code = designInput.trim();
        if (!code) {
            setEntryStatus({ type: "warn", text: "Enter a design code" });
            return;
        }
        setEntryBusy(true);
        try {
            const row = await mainAPI(code);
            if (row) {
                const shown = row.DesignCode ?? code;
                setEntryStatus({ type: "ok", text: `Added ${shown}${row.ProdName ? " · " + row.ProdName : ""}` });
                setDesignInput("");
                setTimeout(() => tagNoRef.current?.focus(), 60);
                return;
            }
            const home = await homeBillData(code);
            if (home) {
                setDesignInput("");
                setEntryStatus(null);
                return;
            }
            setEntryStatus({ type: "err", text: `${code} was not found` });
            setTimeout(() => tagNoRef.current?.select?.(), 60);
        } finally {
            setEntryBusy(false);
        }
    };

    const handleHomeBillSave = () => {
        if (Number(qty) === 0) {
            toastError("Check Quantity");
            return;
        }
        const homeData = {
            ACITY: "-",
            AGENTNAME: "-",
            Area: "-",
            BOXES: null,
            BRANCHNAME: "-",
            CASERATECP: null,
            CASERATEDP: null,
            CASERATERP: null,
            CGSTAMT: 0,
            CGSTRATE: 0,
            COLOUR: "-",
            CPrice: rate,
            CTOKEN: 0,
            Code: "-",
            CompName: brandName,
            DPrice: 0,
            DesignCode: designCode,
            Disamt: disAmt,
            Disper: disCount,
            ECODE: "-",
            ENTRYDATE: "-",
            ENTRYNO: 0,
            EQTY: 0,
            EXPDATE: null,
            FITTING: "-",
            FLOOR: "-",
            FYEAR: "-",
            GST: 0,
            GSTAMT: 0,
            GrosDPrice: 0,
            GroupName: "-",
            HID: 0,
            HSNCODE: hsnCode,
            ICODETYPE: "-",
            IGSTAMT: 0,
            IGSTRATE: 0,
            InvNO: "-",
            Invdate: "-",
            ItemCode: "0",
            LASTBILLDATE: "-",
            LENGTH: null,
            LOOSEPCS: null,
            LQTY: null,
            OFFER_DIS: 0,
            OQTY2: 0,
            OQTY3: 0,
            OQTY4: null,
            PACKPCS: null,
            PNTMODEL: "-",
            PQTY: 0,
            PTYPE: pTypeName,
            Partyname: "-",
            ProdName: productName,
            Prodsize: "-",
            Qty: qty,
            RESELLERPRICE: null,
            RQTY: null,
            SEMIWSALERPRICE: null,
            SET_ITEMS: false,
            SGSTAMT: 0,
            SGSTRATE: 0,
            SLEVES: "-",
            SMODEL: "-",
            SNO: 0,
            TOKEN: 0,
            USTATUS: null,
            WPER: 0,
            WPRICE: 0,
            stock: qty,
            cloud_upload: false,
            mesure: null,
            offer: amount,
            taans: false,
        };
        setHomeDetailsOpen(false);
        setQty(0);
        setAmount(0);
        setDisCount(0);
        setRate(0);
        setDisAmt(0);
        setProductName("");
        setDesignCode("");
        setPTypeName("");
        setHsnCode("");
        setBrandName("");
        setProductData([]);
        setBarCodeData((prev) => [
            ...prev,
            {
                ...homeData,
                GSTApplied: getAppliedGST(homeData, false),
                SMCODE: smCodeData[0]?.CODE ?? "",
            },
        ]);
    };

    const resetPayments = () => {
        setCashValue("CASH");
        setCardAmt(0);
        setUpiAmt(0);
        setOnlineAmt(0);
        setCreditAmt(0);
    };

    const handleDelete = (indexToDelete) => {
        setBarCodeData((prev) => prev.filter((_, i) => i !== indexToDelete));
        resetPayments();
    };

    /* ------------------ load an existing bill (return / sale) ------------------ */

    // isSale=true  -> rows come from BILL_ITEMS_DET (the original sale)
    // isSale=false -> rows come from RETURN_DATA (a saved return bill)
    const rowToItem = (item, isSale) => ({
        ACITY: "-",
        AGENTNAME: "-",
        Area: item?.AREA,
        BOXES: item?.BOXES,
        BRANCHNAME: "-",
        CASERATECP: null,
        CASERATEDP: null,
        CASERATERP: null,
        CGSTAMT: item?.CGSTAMT,
        CGSTRATE: item?.CGSTRATE,
        COLOUR: item?.COLOUR,
        CPrice: item?.rate,
        CTOKEN: 0,
        Code: item?.code,
        CompName: item?.CompName,
        DPrice: item?.DPrice,
        DesignCode: item?.ITEMCODE,
        Disamt: item?.ITEM_DIS,
        Disper: item?.DISPER,
        ECODE: item?.ECODE,
        ENTRYDATE: "-",
        ENTRYNO: 0,
        EQTY: 0,
        EXPDATE: null,
        FITTING: item?.FITTING,
        FLOOR: item?.FLOOR,
        FYEAR: "-",
        GST: item?.GST,
        GSTAMT: item?.GSTAMT,
        GrosDPrice: item?.ITEM_ACTRATE,
        GroupName: item?.GroupName,
        HID: item?.HID,
        HSNCODE: item?.HSNCODE,
        ICODETYPE: "-",
        IGSTAMT: item?.IGSTAMT,
        IGSTRATE: item?.IGSTRATE,
        InvNO: item?.INVNO,
        Invdate: item?.INVDATE,
        ItemCode: "0",
        LASTBILLDATE: "-",
        LENGTH: null,
        LOOSEPCS: null,
        LQTY: null,
        OFFER_DIS: 0,
        OQTY2: isSale ? Number(item?.PCSPRICE) : 0,
        OQTY3: isSale ? Number(item?.CASEPRICE) : 0,
        OQTY4: null,
        PACKPCS: null,
        PNTMODEL: "-",
        PQTY: 0,
        PTYPE: item?.PTYPE,
        Partyname: item?.PARTYNAME,
        ProdName: item?.itemname,
        Prodsize: item?.PRODSIZE,
        Qty: item?.qty,
        RESELLERPRICE: null,
        RQTY: null,
        SEMIWSALERPRICE: null,
        SET_ITEMS: false,
        SGSTAMT: item?.SGSTAMT,
        SGSTRATE: item?.SGSTRATE,
        SLEVES: item?.SLEVES,
        SMODEL: isSale ? item?.STATION : "-",
        SMCODE: item?.SMCODE ? String(item.SMCODE) : "",
        SNO: 0,
        TOKEN: item?.TOKEN,
        USTATUS: null,
        WPER: 0,
        WPRICE: 0,
        stock: item?.qty,
        cloud_upload: false,
        mesure: null,
        offer: item?.ITEM_ACTRATE,
        taans: false,
    });

    const appendRows = async (rows, isSale) => {
        const mapped = rows.map((r) => rowToItem(r, isSale));
        for (const item of mapped) {
            if (item.DesignCode) await qtyAPI(item.DesignCode);
        }
        setBarCodeData((prev) => [
            ...prev,
            ...mapped.map((item) => ({ ...item, GSTApplied: getAppliedGST(item, true) })),
        ]);
    };

    // payment mode of a saved return (online part)
    const loadOnlinePayment = async (no, stype) => {
        try {
            const response = await getTable("ORG_CARDDET", `BILLNO=${no} AND STYPE='${stype}'`);
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                const ptype = data[0]?.PTYPE;
                const amt = data[0]?.AMT;
                if (ptype === "CARD") {
                    setCashValue("CARD");
                    setCardAmt(amt);
                } else if (ptype === "UPI") {
                    setCashValue("UPI");
                    setUpiAmt(amt);
                } else if (ptype === "ONLINE") {
                    setCashValue("ONLINE");
                    setOnlineAmt(amt);
                } else {
                    setCashValue("CASH");
                }
            }
        } catch (error) {
            console.error("Error fetching online payment:", error);
        }
    };

    // credit part of a saved return (the due is stored in NAMA)
    const loadCreditPayment = async (no, salesType) => {
        try {
            const response = await getTable(
                "TRANS_ENTRY_DATA_CUST",
                `ENTRYNO=${no} AND SALESTYPE='${salesType}'`,
            );
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                const due = Number(data[0]?.NAMA) || 0;
                if (due > 0) {
                    setCashValue("CREDIT");
                    setCreditAmt(due);
                    setCardAmt(0);
                    setUpiAmt(0);
                    setOnlineAmt(0);
                }
                userGetDetailsAPI(data[0]?.AGENTNAME);
            }
        } catch (error) {
            console.error("Error fetching ledger:", error);
        }
    };

    const openReturnBill = async (no) => {
        try {
            const det = await getTable("RETURN_DATA", `BILLNO=${no}`);
            if (!Array.isArray(det.data) || det.data.length === 0) {
                toastError("Return bill not found");
                return;
            }
            resetAll();
            setSheetOpen(false);
            setInvNo(Number(no));
            await appendRows(det.data, false);

            const mast = await getTable("RETURN_MAST", `BILLNO=${no}`);
            const m = mast.data?.[0];
            if (m) {
                userGetDetailsAPI(m?.PhoneNo);
                setDisPer(Number(m?.DISPER) || 0);
                setTotalDiscountAmt(Number(m?.DISAMT) || 0);
                const d = pick(m, "billdate", "BillDate", "BILLDATE");
                if (d) setBillDate(d);
            }
            await loadOnlinePayment(no, "SALE RETURN");
            await loadCreditPayment(no, "SALE RETURN");
            toastOk(`Return #${no} loaded`);
        } catch (error) {
            console.error("Error loading return bill:", error);
            toastError("Could not load the return bill");
        }
    };

    // starts a NEW return from an original sale: screen is cleared first,
    // dated today, and payment stays CASH (the sale's own payment is not copied)
    const openSaleBill = async (no) => {
        try {
            const det = await getTable("BILL_ITEMS_DET", `BILLNO=${no} AND TYPE='B'`);
            if (!Array.isArray(det.data) || det.data.length === 0) {
                toastError("Sale bill not found");
                return;
            }
            resetAll();
            setSheetOpen(false);
            await appendRows(det.data, true);

            const mast = await getTable("CREDIT_BILLMAST", `BILLNO=${no}`);
            const m = mast.data?.[0];
            if (m) {
                userGetDetailsAPI(m?.PhoneNo);
                setDisPer(Number(m?.DISPER) || 0);
                setTotalDiscountAmt(Number(m?.DISAMT) || 0);
            }
            toastOk(`Sale bill #${no} loaded`);
        } catch (error) {
            console.error("Error loading sale bill:", error);
            toastError("Could not load the sale bill");
        }
    };

    const pickBill = (no) => {
        if (!no) return;
        if (sheetTab === "RETURN") openReturnBill(no);
        else openSaleBill(no);
    };

    /* ---------------------------- bills sheet --------------------------- */

    // same date-range query as the desktop invoice picker, so the list stays small
    useEffect(() => {
        if (!sheetOpen) return;
        if (!sheetFrom || !sheetTo || sheetFrom > sheetTo) {
            setSheetRows([]);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setSheetLoading(true);
            setSheetRows([]);
            try {
                const table = sheetTab === "RETURN" ? "RETURN_MAST" : "CREDIT_BILLMAST";
                const extra = sheetTab === "SALE" ? "AND APPNO=0" : "";
                const response = await axios.get(
                    `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=${table}&where=BILLDATE>='${sheetFrom}' AND BILLDATE<='${sheetTo}' ${extra}&order=BILLNO DESC`,
                    H,
                );
                if (!cancelled) {
                    setSheetRows(Array.isArray(response.data) ? response.data : []);
                }
            } catch (error) {
                console.error("Error fetching bills:", error);
            } finally {
                if (!cancelled) setSheetLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheetOpen, sheetTab, sheetFrom, sheetTo]);

    const closeSheet = () => {
        setSheetOpen(false);
        setSheetSearch("");
    };

    const setRange = (days) => {
        setSheetTo(dayjs().format("YYYY-MM-DD"));
        setSheetFrom(dayjs().subtract(days, "day").format("YYYY-MM-DD"));
    };

    const visibleRows = sheetRows
        .filter(
            (r) =>
                !sheetSearch ||
                String(pick(r, "billno", "BILLNO", "BillNo")).includes(sheetSearch),
        )
        .slice(0, 80);

    /* ------------------------------ totals ------------------------------ */

    useEffect(() => {
        let mrp = 0;
        let dis = 0;
        let q = 0;
        let basicTotal = 0;
        let gstTotal = 0;
        const groups = new Map();

        barCodeData.forEach((item) => {
            const s = Number(item.stock) || 0;
            const offer = Number(item.offer) || 0;
            const price = Number(item.CPrice) || 0;
            const g = Number(item.GSTApplied) || 0;
            const lineNet = s * offer;
            const basic = lineNet * (100 / (100 + g));
            const tax = basic * (g / 100);

            mrp += s * price;
            dis += s * (Number(item.Disamt) || 0);
            q += s;
            basicTotal += basic;
            gstTotal += tax;

            if (!groups.has(g)) {
                groups.set(g, {
                    GST: g,
                    CGST_RATE: g / 2,
                    Basic_amt: 0,
                    CGST_AMT: 0,
                    SGST: g / 2,
                    SGST_AMT: 0,
                    NET_AMT: 0,
                });
            }
            const row = groups.get(g);
            row.Basic_amt += basic;
            row.CGST_AMT += tax / 2;
            row.SGST_AMT += tax / 2;
            row.NET_AMT += lineNet;
        });

        const gross = mrp - dis;
        // ASSUMPTION: when the loaded bill carried a discount %, it is applied on
        // the gross so it follows item edits/deletes; otherwise the stored amount is used.
        const billDisc = Number(disPer) > 0 ? (gross * Number(disPer)) / 100 : Number(totalDiscountAmt) || 0;
        setTotalAmount(mrp);
        setTotalDisAmt(dis);
        setTotalGrossAmt(gross);
        setBillDiscAmt(billDisc);
        setTotalNetAmt(gross - billDisc);
        setTotalQty(q);
        setTotalBasicAmount(basicTotal);
        setTotalGstAmt(gstTotal);
        setTotalCGSTAmt(gstTotal / 2);
        setTotalSGSTAmt(gstTotal / 2);
        setTotalGstData(Array.from(groups.values()));
    }, [barCodeData, disPer, totalDiscountAmt]);

    useEffect(() => {
        setCashAmt(totalNetAmt - cardAmt - upiAmt - onlineAmt - creditAmt);
    }, [totalNetAmt, cardAmt, upiAmt, onlineAmt, creditAmt]);

    useEffect(() => {
        if (!totalNetAmt || !upiLink) return;
        const canvas = qrRef.current?.querySelector("canvas");
        if (canvas) setBase64QR(canvas.toDataURL("image/png"));
    }, [totalNetAmt, upiLink]);

    useEffect(() => {
        lastItemRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [barCodeData.length]);

    useEffect(() => {
        gstCheckAPI();
        returnNoAPI();
        userAPI();
        onlineAccNoAPI();
        commissionAPI();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ------------------- save / update (desktop APIs) ------------------- */

    const buildReturnPayload = (isUpdate) => {
        const billNoValue = isUpdate ? Number(invNo) : 0;

        // dates are built here (not during render) and fall back to today
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        const bd = billDate ? dayjs(billDate) : null;
        const validBill = bd && bd.isValid();
        const billDateStr = validBill ? bd.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
        const masterDate = validBill ? new Date(billDateStr).toISOString() : today.toISOString();

        const calc = (item) => {
            const basic = item.stock * item.offer * (100 / (100 + item.GSTApplied));
            const commission = (Number(item.stock * item.offer) * comm) / 100;
            return {
                basic,
                tax: basic * (item.GSTApplied / 100),
                commToken: item?.TOKEN > 0 ? 0 : commission,
            };
        };

        // sg = sign applied to the quantity / amount columns
        const line = (item, index, sg) => {
            const { basic, tax, commToken } = calc(item);
            return {
                billno: billNoValue,
                billdate: masterDate,
                street: userDetails?.Area || "-",
                custName: userDetails?.Custname || "-",
                sno: index + 1,
                itemcode: item?.DesignCode ? String(item.DesignCode) : "",
                itemname: String(item?.ProdName ?? ""),
                code: String(item?.Code ?? ""),
                compName: String(item?.CompName ?? ""),
                groupName: String(item?.GroupName ?? ""),
                smcode: item?.SMCODE ? String(item.SMCODE) : "-",
                qty: sg * (Number(item?.stock) || 0),
                rate: Number(item?.CPrice) || 0,
                disper: Number(item?.Disper) || 0,
                disAmt: Number(item?.Disamt) || 0,
                actrate: String(item?.offer ?? ""),
                amount: String(item?.offer * item?.stock),
                hid: Number(item?.HID) || 0,
                dPrice: Number(item?.DPrice) || 0,
                comm: sg * Number(commToken),
                token: sg * (Number(item?.TOKEN) || 0),
                ptype: String(item?.PTYPE ?? ""),
                area: String(item?.Area ?? ""),
                partyname: String(item?.Partyname ?? ""),
                type: "RB",
                vatper: 0,
                vatamt: 0,
                floor: String(item?.FLOOR ?? ""),
                wsalebill: false,
                appno: 0,
                invno: String(item?.InvNO ?? ""),
                colour: String(item?.COLOUR ?? ""),
                fitting: String(item?.FITTING ?? ""),
                sleves: String(item?.SLEVES ?? ""),
                ecode: String(item?.ECODE ?? ""),
                prodsize: String(item?.Prodsize ?? ""),
                station: "-",
                iteM_DIS: Number(item?.Disamt) || 0,
                iteM_ACTRATE: Number(item?.offer) || 0,
                scheck: false,
                hsncode: String(item?.HSNCODE ?? ""),
                gst: Number(item?.GSTApplied) || 0,
                gstamt: sg * tax,
                cgstrate: item?.GSTApplied / 2,
                cgstamt: sg * (tax / 2),
                sgstrate: item?.GSTApplied / 2,
                sgstamt: sg * (tax / 2),
                igstrate: 0,
                igstamt: 0,
                basicamt: sg * Number(basic),
                serialno: 0,
                brandcode: "0",
                salestype: "SALE RETURN",
                fsize: "-",
                tsize: "-",
                differ: "-",
                actqty: sg * (Number(item?.stock) || 0),
                refcode: "-",
                state: userDetails?.STATE || "",
                statecode: userDetails?.STATECODE || "",
                tinno: "-",
                idproof: "-",
                panno: "-",
                boxes: Number(item?.BOXES) || 0,
                loosepcs: Number(item?.LOOSEPCS) || 0,
                pfcharges: 0,
                cessper: 0,
                cessamt: 0,
                packpcs: Number(item?.PACKPCS) || 0,
                caseprice: 0,
                pcsprice: 0,
                casepricers: 0,
                pcspricers: 0,
                measurementtype: "-",
                tokenper: 0,
                ustatus: false,
                gstin: "-",
                estbillno: false,
                cloud_upload: true,
                billreduce: 0,
            };
        };

        // UNVERIFIED against the desktop SalesInvoiceReturn: on Update the bill-items
        // rows are stored with negative values.
        const itemsSign = isUpdate ? -1 : 1;
        const returnData = barCodeData.map((item, i) => line(item, i, 1));
        const billItemsDet = barCodeData.map((item, i) => ({
            ...line(item, i, itemsSign),
            billdate: billDateStr,
            street: userDetails?.Area || "",
            custName: userDetails?.Custname || "",
            invdate: billDateStr,
        }));

        const master = {
            billno: billNoValue,
            billdate: masterDate,
            street: userDetails?.Area || "-",
            custName: userDetails?.Custname || "-",
            add1: userDetails?.add1 || "-",
            phoneNo: userDetails?.Mobileno || "-",
            totamt: Number(totalAmount),
            discount: Number(totalDisAmt),
            advAmt: Number(cashAmt),
            finalamt: Number(creditAmt),
            billtype: cashValue === "CREDIT" ? "CREDIT" : "CASH",
            lessAmt: 0,
            smcode: "-",
            pstatus: false,
            ebillno: 0,
            mode: cashValue === "CREDIT" || cashValue === "CASH" ? "CASH" : "CARD",
            accNO: "-",
            cardno: cashValue === "CREDIT" || cashValue === "CASH" ? "0" : "-",
            cardamt: Number(cardAmt || upiAmt || onlineAmt || 0),
            sTax: 0,
            flrcode: "-",
            esmCode: "-",
            days: 0,
            cStatus: true,
            card: 0,
            hid: 0,
            estno: 0,
            adD2: userDetails?.add2 || "-",
            billtime: masterDate,
            uname: loginName ?? "-",
            cancell: "NO",
            ctime: masterDate,
            cuname: "-",
            station: "mobile",
            billstatus: false,
            funame: "-",
            fstation: "-",
            reccash: 0,
            refcash: 0,
            wsalebill: false,
            appno: 0,
            ccause: "-",
            edisamt: 0,
            chqdate: masterDate,
            narration: "-",
            invpayableamt: Number(totalNetAmt),
            invpaidamt: Number(invPaidAmt),
            invrtnamt: 0,
            invbalamt: Number(creditAmt),
            invstatus: true,
            extradiscount: 0,
            prevpoints: 0,
            prevamt: 0,
            billpoints: 0,
            billvalue: 0,
            redimpoints: 0,
            redimamt: 0,
            totpoints: 0,
            totvalue: 0,
            prevcardno: "-",
            adjustments: 0,
            totgst: 0,
            totogstamt: Number(totalGstAmt),
            totcgstrate: 0,
            totcgstamt: Number(totalCGSTAmt),
            totsgstrate: 0,
            totsgstamt: Number(totalSGSTAmt),
            totigstrate: 0,
            totigstamt: 0,
            serialno: 0,
            salestype: "SALE RETURN",
            custstation: userDetails?.STATION || "LOCAL",
            transport: "-",
            custtype: "CUSTOMER",
            stype: "SALE RETURN",
            lrno: "-",
            lrdate: "-",
            shipstreet: "-",
            shipcustname: "-",
            shipadD1: "-",
            shipadD2: "-",
            shipphoneno: "-",
            shiptinno: "-",
            noofbags: "-",
            orderno: "-",
            orderdate: "-",
            dateofsupply: "-",
            placeofsupply: "-",
            company: "-",
            place: "-",
            branch: "-",
            bankaccno: "-",
            ifsccode: "-",
            state: "-",
            statecode: "-",
            shipstate: "-",
            shipstatecode: "-",
            bankname: "-",
            beatarea: "-",
            orddate: "-",
            gstin: "-",
            totbasicamt: Number(totalBasicAmount),
            totamtaftax: 0,
            salesper: 0,
            salesamt: 0,
            grossamt: Number(totalNetAmt),
            roundoff: 0,
            netamt: Number(totalNetAmt),
            tinno: "-",
            idproof: "-",
            panno: "-",
            purchno: "-",
            disamt: Number(billDiscAmt) || 0,
            schemeamt: 0,
            disper: Number(disPer) || 0,
            pinvno: "-",
            m_BTYPE: "-",
            m_BNO: "-",
            m_BDATE: "-",
            includinggst: false,
            acity: "-",
            agentname: "-",
            ustatus: false,
            estbillno: false,
            paymode: "-",
            totqty: totalQty,
            refundamt: 0,
            paidcard: Number(cardAmt),
            paidcheque: 0,
            paidonline: Number(onlineAmt),
            paidupi: Number(upiAmt),
            paidcash: Number(cashAmt),
            balance: Number(creditAmt),
            ctype: "RETAIL",
            withgst: false,
            marginper: 0,
            cloud_upload: true,
            couponredem: 0,
        };

        // ---- accounting entries (UNVERIFIED against the desktop screen) ----
        const commonFields = {
            entrydate: billDateStr,
            entryno: billNoValue,
            vno: String(billNoValue),
            vdate: billDateStr,
            scode: 4,
            description: "-",
            salestype: "SALE RETURN",
            hid: 0,
            sno: 0,
            clouD_UPLOAD: true,
            outstandinG_CHEQUE: "-",
        };
        const bankLedger =
            cashValue === "CARD" ? cardAcc : cashValue === "UPI" ? upiAcc : onlineAcc;
        const bankAmount =
            cashValue === "CARD"
                ? Number(cardAmt)
                : cashValue === "UPI"
                    ? Number(upiAmt)
                    : Number(onlineAmt);
        const entry = (over) => ({
            ...commonFields,
            subgroup: "-",
            maingroup: "",
            particulars: "",
            paymode: "-",
            ...over,
        });
        const isOnline = cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE";
        // tolerance instead of exact equality: decimals can leave 1e-13 behind
        const nothingLeft = Math.abs(totalAmt) < 0.005;

        let singleEntryData = [];
        if (cashValue === "CASH") {
            singleEntryData = [
                entry({
                    ledgername: "CASH SALES",
                    groupname: "CUSTOMER",
                    jamaamount: totalNetAmt,
                    namaamount: 0,
                    tranS_TYPE: "CASH SALES",
                    stype: "CASH SALES",
                }),
                entry({
                    ledgername: "CASH BOOK",
                    groupname: "CASH BOOK",
                    maingroup: "CASH BOOK",
                    jamaamount: totalNetAmt,
                    namaamount: 0,
                    tranS_TYPE: "CASH SALES",
                    stype: "CASH BOOK",
                }),
            ];
        } else if (isOnline && nothingLeft) {
            singleEntryData = [
                entry({
                    ledgername: "ONLINE SALES",
                    groupname: "CUSTOMER",
                    jamaamount: totalNetAmt,
                    namaamount: 0,
                    tranS_TYPE: "ONLINE SALES",
                    stype: "ONLINE SALES",
                }),
                entry({
                    ledgername: bankLedger,
                    groupname: "CUSTOMER",
                    jamaamount: 0,
                    namaamount: bankAmount,
                    tranS_TYPE: "ONLINE SALES",
                    stype: "BANK TRANS",
                    paymode: cashValue,
                }),
            ];
        } else if (isOnline) {
            singleEntryData = [
                entry({
                    ledgername: "ONLINE SALES",
                    groupname: "CUSTOMER",
                    jamaamount: totalNetAmt,
                    namaamount: 0,
                    tranS_TYPE: "ONLINE SALES",
                    stype: "ONLINE SALES",
                }),
                entry({
                    ledgername: bankLedger,
                    groupname: "CUSTOMER",
                    jamaamount: 0,
                    namaamount: bankAmount,
                    tranS_TYPE: "CASH SALES",
                    stype: "BANK TRANS",
                    paymode: cashValue,
                }),
                entry({
                    ledgername: "CASH BOOK",
                    groupname: "CASH BOOK",
                    maingroup: "CASH BOOK",
                    jamaamount: totalNetAmt - cardAmt - upiAmt - onlineAmt - creditAmt,
                    namaamount: 0,
                    tranS_TYPE: "CASH SALES",
                    stype: "CASH BOOK",
                }),
            ];
        } else if (cashValue === "CREDIT" && creditAmt > 0 && cashAmt > 0) {
            singleEntryData = [
                entry({
                    ledgername: "CASH SALES",
                    groupname: "CUSTOMER",
                    jamaamount: Number(totalNetAmt),
                    namaamount: 0,
                    tranS_TYPE: "   SALES",
                    stype: "CASH SALES",
                }),
                entry({
                    ledgername: "CASH BOOK",
                    groupname: "CASH BOOK",
                    maingroup: "CASH BOOK",
                    jamaamount: Number(cashAmt),
                    namaamount: 0,
                    tranS_TYPE: "CASH SALES",
                    stype: "CASH BOOK",
                }),
                entry({
                    ledgername: userDetails?.Custname,
                    groupname: "CUSTOMER",
                    maingroup: "CASH BOOK",
                    jamaamount: 0,
                    namaamount: Number(creditAmt),
                    tranS_TYPE: "CASH SALES",
                    stype: "CUSTOMER DUE",
                }),
            ];
        } else if (cashValue === "CREDIT" && creditAmt > 0) {
            singleEntryData = [
                entry({
                    ledgername: "CASH SALES",
                    groupname: "CUSTOMER",
                    jamaamount: Number(totalNetAmt),
                    namaamount: 0,
                    tranS_TYPE: "CASH SALES",
                    stype: "CASH SALES",
                }),
                entry({
                    ledgername: userDetails?.Custname,
                    groupname: "CUSTOMER",
                    maingroup: "CASH BOOK",
                    jamaamount: 0,
                    namaamount: Number(creditAmt),
                    tranS_TYPE: "CASH SALES",
                    stype: "CUSTOMER DUE",
                }),
            ];
        }

        const orgCardDet = isOnline
            ? [
                {
                    billno: billNoValue,
                    billdate: masterDate,
                    bal: Number(totalNetAmt),
                    sno: 1,
                    paymode: cashValue,
                    accno: bankLedger,
                    narr: "-",
                    amt: bankAmount,
                    cntrname: "",
                    stype: "SALE RETURN",
                    ptype: cashValue,
                    cloud_upload: true,
                },
            ]
            : [];

        const transEntryDataCust =
            creditAmt > 0
                ? [
                    {
                        area: userDetails?.Area || "-",
                        partyname: userDetails?.Custname || "-",
                        billno: String(billNoValue),
                        billdate: masterDate,
                        entryno: billNoValue,
                        entrydate: masterDate,
                        particulars: "SALE RETURN",
                        jamaqty: 0,
                        namaqty: Number(totalQty) || 0,
                        jama: 0,
                        nama: Number(creditAmt) || 0,
                        narration: "-",
                        hid: 0,
                        agentcity: "-",
                        agentname: String(userDetails?.Mobileno || "-"),
                        tranS_TYPE: "SALE RETURN",
                        cusT_TYPE: "CUSTOMER",
                        sno: 0,
                        pstatus: "",
                        salestype: "SALE RETURN",
                        chequeno: "-",
                        chqdate: masterDate,
                        chqamt: 0,
                        chqstatus: false,
                        accno: "-",
                        chqcancell: "NO",
                        description: "-",
                        cloud_upload: false,
                    },
                ]
                : [];

        return {
            master,
            returnData,
            billItemsDet,
            singleEntryData,
            orgCardDet,
            transEntryDataCust,
        };
    };

    // one call to the combined endpoint - the server writes every table.
    // Does NOT reset the screen, so printing can still use the current state.
    const submitReturn = async () => {
        const isUpdate = Boolean(invNo);
        const response = await axios.post(
            `${CREATE_jwel}/api/SaleReturn/${isUpdate ? "Update" : "Save"}`,
            buildReturnPayload(isUpdate),
            JSON_H,
        );
        if (response?.data?.isInsert === true) {
            toastOk(isUpdate ? "Return updated" : "Return saved");
            return true;
        }
        toastError("Return could not be saved. Please try again.");
        return false;
    };

    const validateReturn = () => {
        if (!barCodeData?.length) {
            toastError("Please add Design Details.");
            return false;
        }
        if (cashValue === "CREDIT" && !hasCustomer) {
            toastError("Please enter customer details for CREDIT bill.");
            return false;
        }
        // CARD / UPI / ONLINE / CREDIT picked but no amount entered: nothing would be posted
        const amtByMode = { CARD: cardAmt, UPI: upiAmt, ONLINE: onlineAmt, CREDIT: creditAmt };
        if (cashValue !== "CASH" && !(Number(amtByMode[cashValue]) > 0)) {
            toastError(`Enter the ${String(cashValue).toLowerCase()} amount in payment mode.`);
            return false;
        }
        if (cashAmt < -0.005) {
            toastError("Payment amounts are more than the return amount.");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (saving || !validateReturn()) return;
        try {
            setSaving(true);
            if (await submitReturn()) resetAll();
        } catch (error) {
            console.error("Error saving return:", error);
            toastError("Something went wrong while saving the return.");
        } finally {
            setSaving(false);
        }
    };

    /* ------------------------------ printing ----------------------------- */

    const handlePrintClick = () => {
        if (printSaving || !validateReturn()) return;
        setPrintOpen(true);
    };

    // save first; only print once the save really succeeded
    const handlePrintSave = async (method) => {
        setPrintOpen(false);
        try {
            setPrintSaving(true);
            const nextNo = invNo ? invNo : ((await returnNoAPI()) ?? returnNo);
            if (!(await submitReturn())) return;
            try {
                if (method === "PDF") await downloadReturnPdf(nextNo);
                else runEpson(nextNo);
            } catch (error) {
                console.error("Print failed:", error);
                toastError("Saved, but printing failed.");
            }
            resetAll();
        } catch (error) {
            console.error("Error saving return:", error);
            toastError("Something went wrong while saving the return.");
        } finally {
            setPrintSaving(false);
        }
    };

    // Epson ePOS over the shop network. The helper prints a "TAX INVOICE" title, and the
    // printer socket is plain ws:// (port 8008), which Chrome blocks from an https page.
    const runEpson = (no) => {
        let ip = localStorage.getItem("ipAddress") || localStorage.getItem("printerIp");
        if (!ip) {
            ip = (window.prompt("Epson printer IP address") || "").trim();
            if (!ip) return;
            localStorage.setItem("printerIp", ip);
        }
        printReceipt(
            ip, address, address1, city, gstNo, userPhone, no, invNo || 0, billDate,
            userDetails, "", userName, "",
            barCodeData, totalAmount, totalDisAmt + billDiscAmt, totalNetAmt, totalQty,
            base64Images, base64QR, totalGstData,
            ins, ins1, ins2, ins3, ins4, ins5, ins6,
            gstValue, cashAmt, cardAmt, onlineAmt, upiAmt, creditAmt,
            { docType: "RETURN", onError: toastError },
        );
    };

    const downloadReturnPdf = async (nextNo) => {
        const el = document.createElement("div");
        const clean = (v) => (v && v !== "-" ? v : "");
        const rows = barCodeData
            .map(
                (it, i) => `<tr>
          <td>${i + 1}</td>
          <td style="text-align:left">${esc(it.ProdName)}<br/><small>${esc(it.DesignCode)}${clean(it.HSNCODE) ? ` · HSN ${esc(it.HSNCODE)}` : ""}</small></td>
          <td>${it.stock}</td>
          <td>${Number(it.CPrice).toFixed(2)}</td>
          <td>${Number(it.Disamt).toFixed(2)}</td>
          <td style="text-align:right"><b>${(it.stock * it.offer).toFixed(2)}</b></td>
        </tr>`,
            )
            .join("");
        const gstRows =
            gstValue === true
                ? totalGstData
                    .map(
                        (r) =>
                            `<tr><td>${r.Basic_amt.toFixed(2)}</td><td>${r.CGST_RATE}%</td><td>${r.CGST_AMT.toFixed(2)}</td><td>${r.SGST}%</td><td>${r.SGST_AMT.toFixed(2)}</td><td>${r.NET_AMT.toFixed(2)}</td></tr>`,
                    )
                    .join("")
                : "";
        const pay = (label, v) =>
            v > 0
                ? `<div style="display:flex;justify-content:space-between"><span>${label}</span><span>${Number(v).toFixed(2)}</span></div>`
                : "";

        el.innerHTML = `
      <style>
        table{width:100%;border-collapse:collapse;font-family:Arial,sans-serif;font-size:9px}
        th,td{border:1px solid #222;padding:3px;text-align:center}
        th{background:#162566;color:#fff;font-size:10px}
      </style>
      <div style="width:90mm;margin:0 auto;padding:0 5mm;box-sizing:border-box;font-family:Arial,sans-serif;font-size:10px">
        <div style="text-align:center;margin-bottom:5px">
          ${base64Images ? `<img src="${base64Images}" style="width:160px;height:160px;object-fit:contain"/>` : ""}
          <div style="font-weight:bold;font-size:12px">${esc(address)}</div>
          <div style="font-weight:bold;font-size:12px">${esc(address1)}</div>
          <div style="font-weight:bold;font-size:12px">${esc(city)}</div>
          ${isValidValue(gstNo) ? `<div style="font-weight:bold;font-size:12px">${esc(gstNo)}</div>` : ""}
          <div style="font-weight:bold;font-size:12px">PHONE NO: ${esc(userPhone)}</div>
        </div>
        <div style="border:1px solid #222;padding:6px;margin-bottom:5px;font-size:11px">
          <div style="background:#162566;color:#fff;text-align:center;font-weight:bold;padding:5px;margin:-6px -6px 6px">SALE RETURN</div>
          <div>Return No : <b style="color:#d00">#${invNo ? invNo : nextNo}</b></div>
          <div>Date : <b>${dayjs(billDate || undefined).format("DD-MMM-YYYY hh:mm A")}</b></div>
          <div>Customer : <b>${esc(userDetails?.Custname || "-")}</b></div>
          <div>Contact : <b>${esc(userDetails?.Mobileno || "-")}</b></div>
        </div>
        <table>
          <thead><tr><th>#</th><th>PARTICULARS</th><th>QTY</th><th>RATE</th><th>DISC</th><th>AMOUNT</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div style="border:1px solid #222;margin-top:5px;font-size:11px">
          <div style="padding:6px">
            <div style="display:flex;justify-content:space-between"><span>Total Amount</span><span>${totalAmount.toFixed(2)}</span></div>
            <div style="display:flex;justify-content:space-between"><span>Discount</span><span>${(totalDisAmt + billDiscAmt).toFixed(2)}</span></div>
          </div>
          <div style="background:#52bd91;color:#fff;font-weight:bold;display:flex;justify-content:space-between;padding:6px;font-size:13px"><span>Net Amount</span><span>${totalNetAmt.toFixed(2)}</span></div>
        </div>
        <div style="margin-top:5px;font-weight:bold">Total Qty : ${totalQty}</div>
        ${gstRows ? `<table style="margin-top:8px"><thead><tr><th>BASIC</th><th>CGST %</th><th>CGST</th><th>SGST %</th><th>SGST</th><th>NET</th></tr></thead><tbody>${gstRows}</tbody></table>` : ""}
        <div style="border:1px solid #222;margin-top:6px;padding:6px;font-size:11px">
          ${pay("Cash", cashAmt)}${pay("Card", cardAmt)}${pay("UPI", upiAmt)}${pay("Online", onlineAmt)}${pay("Credit", creditAmt)}
        </div>
        <div style="margin-top:8px;font-size:9px">
          <div style="text-align:center;font-size:12px">Terms &amp; Conditions</div>
          <ul>${[ins, ins1, ins2, ins3, ins4, ins5, ins6].map((t) => `<li>${esc(t)}</li>`).join("")}</ul>
        </div>
        <div style="background:#add8e6;text-align:center;padding:8px;border-radius:10px;font-size:12px">Thank you, please visit again!</div>
      </div>`;

        const opt = {
            margin: [0, 5, 0, 5],
            filename: `Return-${invNo || nextNo}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: [90, 297], orientation: "portrait" },
        };
        await html2pdf().set(opt).from(el).save();
    };

    /* ------------------------ logo -> base64 (for print) ------------------------ */

    const fetchWithRetry = async (url, retries = 3, delay = 300) => {
        for (let i = 0; i < retries; i++) {
            try {
                const res = await fetch(url, { mode: "cors" });
                if (res.ok) return res;
            } catch (e) {
                console.warn(`Retry ${i + 1} for ${url}`);
            }
            await new Promise((r) => setTimeout(r, delay));
        }
        throw new Error("Failed after retries: " + url);
    };

    const resizeBase64Img = (base64Str, maxWidth = 500, maxHeight = 500) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                let width = img.width;
                let height = img.height;
                if (width > maxWidth || height > maxHeight) {
                    if (width / height > maxWidth / maxHeight) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    } else {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/jpeg", 0.85));
            };
            img.onerror = reject;
            img.src = base64Str;
        });

    const urlToBase64 = async (url) => {
        const cleanUrl = decodeURIComponent(url);
        let blob;
        try {
            // direct first (works when the image host sends CORS headers)
            const direct = await fetch(cleanUrl, { mode: "cors" });
            if (!direct.ok) throw new Error("direct fetch failed");
            blob = await direct.blob();
        } catch (e) {
            // fallback: third-party proxy
            const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(cleanUrl)}`;
            blob = await (await fetchWithRetry(proxyUrl, 3, 800)).blob();
        }
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    resolve(await resizeBase64Img(reader.result));
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    useEffect(() => {
        if (!singleImage) return;
        const convertImage = async () => {
            try {
                setBase64Images(await urlToBase64(singleImage));
            } catch (err) {
                console.error("Image conversion failed:", err);
            }
        };
        convertImage();
    }, [singleImage]);

    /* ------------------------- small UI handlers ------------------------ */

    const handleCashOpen = () => setCashOpen(true);
    const handleCashCancel = () => setCashOpen(false);
    const handleQrcodeOpen = () => setQrCodeOpen(true);
    const handleQrcodeCancel = () => setQrCodeOpen(false);

    const handleCardOpen = (code) => {
        setCardOpen(true);
        const g = code.GSTApplied;
        const basic = code.stock * code.offer * (100 / (100 + g));
        setTitleValue(code.DesignCode);
        setGst(g);
        setGstAmt(basic * (g / 100));
        setCGstRate(g / 2);
        setCGstAmt((basic * (g / 100)) / 2);
        setSGstRate(g / 2);
        setSGstAmt((basic * (g / 100)) / 2);
        setIGstRate(g);
        setIGstAmt(0);
        setBasicAmt(basic);
        setNetAmt(code.stock * code.offer);
        setSmCodeView(code?.SMCODE);
    };
    const handleCardCancel = () => setCardOpen(false);

    const handleMobileNoKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            searchRef.current?.click();
        }
    };

    const resetAll = () => {
        returnNoAPI();
        commissionAPI();
        gstCheckAPI();
        onlineAccNoAPI();
        setBarCodeData([]);
        setQtyData([]);
        setDesignInput("");
        setSmDraft("");
        setSmCodeData([]);
        setEntryStatus(null);
        setSmStatus(null);
        setInvNo(0);
        setBillDate();
        setDisPer(0);
        setTotalDiscountAmt(0);
        setCashAmt(0);
        resetPayments();
        setCreditCashValue("CREDIT");
        setCreditCashAmt(0);
        setCreditCardAmt(0);
        setCreditUpiAmt(0);
        setCreditOnlineAmt(0);
        setSwitchCredit("Cash");
        clearCustomer();
    };

    const handleResetClick = () => {
        if (barCodeData.length && !window.confirm("Clear this return and start over?")) return;
        resetAll();
    };

    /* ------------------------------- view ------------------------------- */

    const custInitial = (userDetails?.Custname || "?").trim()[0]?.toUpperCase();

    return (
        <div>
            {contextHolder}
            <Header setOpen={setOpen} />
            <SidebarDrawer
                open={open}
                toggleDrawer={toggleDrawer}
                singleImage={singleImage}
                userArea={userArea}
                userName={userName}
            />

            <div className="bcs">
                <style>{`
          @import url("https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap");

          .bcs, .bcs-sheet-wrap {
            --navy: #121c4d;
            --navy-2: #1d2f7a;
            --ink: #151a36;
            --muted: #7b84a0;
            --paper: #edf2fa;
            --card: #ffffff;
            --line: #e0e7f4;
            --tint: #f3f6fc;
            --mint: #43bd98;
            --mint-deep: #229a79;
            --mint-soft: #e4f6ef;
            --gold: #e0b04f;
            --gold-deep: #c8912b;
            --rose: #e0475f;
            --rose-deep: #b8304a;
            --rose-soft: #fdeef1;
          }
          .bcs {
            background: var(--paper);
            padding: 8px 10px 170px;
            color: var(--ink);
            font-family: "Manrope", "Segoe UI", system-ui, -apple-system, Roboto, Arial, sans-serif;
            font-size: 11.5px;
            min-height: 100%;
          }
          .bcs *, .bcs *::before, .bcs *::after, .bcs-sheet-wrap *, .bcs-sheet-wrap *::before, .bcs-sheet-wrap *::after { box-sizing: border-box; }
          .bcs .ant-btn { font-family: inherit; }
          .bcs button, .bcs-sheet-wrap button, .bcs-sheet-wrap input { font-family: inherit; }

          /* ---- hero ---- */
          .bcs-hero {
            position: relative; overflow: hidden;
            display: flex; align-items: center; justify-content: space-between; gap: 8px;
            padding: 9px 10px; border-radius: 14px; color: #fff;
            background: linear-gradient(135deg, var(--navy) 0%, var(--navy-2) 100%);
            box-shadow: 0 8px 18px -12px rgba(18, 28, 77, 0.8);
          }
          .bcs-hero::after {
            content: ""; position: absolute; right: -26px; top: -34px; width: 100px; height: 100px;
            border-radius: 50%; background: radial-gradient(circle, rgba(224,71,95,0.4), transparent 70%);
            pointer-events: none;
          }
          .bcs-hero-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; position: relative; z-index: 1; }
          .bcs-hero-label { font-size: 9.5px; font-weight: 600; letter-spacing: 0.4px; opacity: 0.7; }
          .bcs-hero-row { display: flex; align-items: baseline; gap: 8px; }
          .bcs-hero-no { font-size: 20px; font-weight: 800; line-height: 1.1; font-variant-numeric: tabular-nums; }
          .bcs-hero-date { font-size: 9.5px; font-weight: 600; padding: 2px 7px; border-radius: 999px; background: rgba(255,255,255,0.14); }
          .bcs-hero-actions { display: flex; gap: 6px; position: relative; z-index: 1; }
          .bcs-ico {
            width: 32px; height: 32px; display: grid; place-items: center; padding: 0;
            border-radius: 10px; color: #fff; cursor: pointer;
            background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.18);
            transition: transform .15s ease, background .15s ease;
          }
          .bcs-ico:active { transform: scale(0.92); background: rgba(255,255,255,0.22); }
          .bcs-ico.mint { color: #8df0cf; }
          .bcs-ico.gold { color: #ffdf8a; }

          /* ---- customer strip ---- */
          .bcs-cust { display: flex; gap: 6px; margin-top: 8px; }
          .bcs-cust-main {
            flex: 1; min-width: 0; display: flex; align-items: center; gap: 9px; padding: 7px 9px;
            text-align: left; cursor: pointer; color: var(--ink);
            background: var(--card); border: 1px solid var(--line); border-radius: 14px;
            box-shadow: 0 6px 16px -14px rgba(18, 28, 77, 0.5);
          }
          .bcs-cust-main:active { background: var(--tint); }
          .bcs-avatar {
            width: 30px; height: 30px; flex-shrink: 0; display: grid; place-items: center;
            font-size: 13px; font-weight: 800; color: #fff; border-radius: 10px;
            background: linear-gradient(135deg, var(--navy), var(--navy-2));
          }
          .bcs-avatar.empty { color: var(--muted); background: var(--tint); border: 1px dashed #c9d3ea; }
          .bcs-cust-txt { flex: 1; min-width: 0; display: flex; flex-direction: column; }
          .bcs-cust-txt b { font-size: 12px; font-weight: 800; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .bcs-cust-txt small { font-size: 10px; font-weight: 600; color: var(--muted); }
          .bcs-cust-act {
            width: 44px; flex-shrink: 0; display: grid; place-items: center; cursor: pointer; padding: 0;
            color: var(--rose); background: var(--card); border: 1px solid var(--line); border-radius: 14px;
          }
          .bcs-cust-act:active { background: var(--tint); }
          .bcs-cust-act.edit { color: var(--navy-2); }

          /* ---- entry tiles ---- */
          .bcs-deck { display: grid; grid-template-columns: 1.25fr 1fr; gap: 8px; margin-top: 8px; }
          .bcs-tile {
            position: relative; overflow: hidden; min-height: 88px; padding: 11px 12px;
            display: flex; flex-direction: column; justify-content: space-between; gap: 10px;
            text-align: left; cursor: pointer; border-radius: 16px; border: 0;
            transition: transform .15s ease;
          }
          .bcs-tile:active { transform: scale(0.97); }
          .bcs-tile::after {
            content: ""; position: absolute; right: -22px; bottom: -26px; width: 84px; height: 84px;
            border-radius: 50%; pointer-events: none;
          }
          .bcs-tile.design {
            color: #fff; background: linear-gradient(135deg, var(--navy), var(--navy-2));
            box-shadow: 0 10px 20px -14px rgba(18,28,77,0.9);
          }
          .bcs-tile.design::after { background: radial-gradient(circle, rgba(67,189,152,0.45), transparent 70%); }
          .bcs-tile.sm {
            color: var(--ink); background: var(--card); border: 1px solid var(--line);
            box-shadow: 0 6px 16px -14px rgba(18, 28, 77, 0.5);
          }
          .bcs-tile.sm::after { background: radial-gradient(circle, rgba(224,176,79,0.28), transparent 70%); }
          .bcs-tile-top { display: flex; align-items: center; justify-content: space-between; gap: 6px; position: relative; z-index: 1; }
          .bcs-tile-ico {
            width: 32px; height: 32px; display: grid; place-items: center; border-radius: 11px;
          }
          .bcs-tile.design .bcs-tile-ico { color: #8df0cf; background: rgba(255,255,255,0.14); }
          .bcs-tile.sm .bcs-tile-ico { color: var(--gold-deep); background: #fbf1da; }
          .bcs-tile-badge {
            padding: 2px 9px; font-size: 10px; font-weight: 800; border-radius: 999px;
            background: rgba(255,255,255,0.16);
          }
          .bcs-tile-badge.set { color: var(--mint-deep); background: var(--mint-soft); }
          .bcs-tile-txt { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 1px; min-width: 0; }
          .bcs-tile-txt b { font-size: 14px; font-weight: 800; }
          .bcs-tile-txt small { font-size: 10.5px; font-weight: 600; opacity: 0.72; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

          /* ---- list ---- */
          .bcs-list-head { display: flex; align-items: center; justify-content: space-between; margin: 11px 3px 6px; }
          .bcs-list-title { font-size: 12px; font-weight: 800; color: var(--navy); }
          .bcs-count {
            min-width: 20px; height: 20px; padding: 0 6px; display: grid; place-items: center;
            font-size: 10.5px; font-weight: 800; color: #fff; border-radius: 999px;
            background: linear-gradient(135deg, var(--rose), var(--rose-deep));
          }
          .bcs-empty { padding: 12px; text-align: center; border-radius: 14px; background: var(--card); border: 1px dashed #cdd7ee; }
          .bcs-empty img { width: 62px; opacity: 0.85; }
          .bcs-empty p { margin: 4px 0 0; font-size: 11px; color: var(--muted); font-weight: 600; }

          .bcs-item {
            position: relative; margin-bottom: 8px; padding: 8px 9px 8px 12px; border-radius: 14px;
            background: var(--card); border: 1px solid var(--line); overflow: hidden;
            box-shadow: 0 6px 14px -12px rgba(18,28,77,0.5); scroll-margin-bottom: 170px;
          }
          .bcs-item::before {
            content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3.5px;
            background: linear-gradient(180deg, var(--rose), var(--rose-deep));
          }
          .bcs-item-top { display: flex; align-items: center; gap: 6px; justify-content: space-between; }
          .bcs-item-left, .bcs-item-right { display: flex; gap: 6px; align-items: center; min-width: 0; }
          .bcs-idx {
            min-width: 20px; height: 20px; padding: 0 6px; display: grid; place-items: center;
            font-size: 9.5px; font-weight: 800; color: #fff; border-radius: 999px;
            background: linear-gradient(135deg, var(--navy), var(--navy-2));
          }
          .bcs-code {
            min-width: 0; padding: 3px 8px; font-size: 11.5px; font-weight: 800; letter-spacing: 0.3px;
            color: #0d5c43; background: var(--mint-soft); border-radius: 8px;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          }
          .bcs-sm {
            padding: 2px 7px; font-size: 9.5px; font-weight: 800; color: var(--navy-2);
            background: #e9edfb; border-radius: 999px; white-space: nowrap;
          }
          .bcs-mini {
            width: 30px; height: 30px; display: grid; place-items: center; padding: 0; cursor: pointer;
            border-radius: 50%; border: 0; color: var(--rose); background: var(--rose-soft);
          }
          .bcs-mini.tax { color: var(--navy-2); background: #e9edfb; }
          .bcs-name { margin: 5px 1px 6px; font-size: 10.5px; font-weight: 600; color: var(--muted); line-height: 1.3; }
          .bcs-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; }
          .bcs-cell { padding: 3px 6px; background: var(--tint); border-radius: 8px; min-width: 0; }
          .bcs-cell span { display: block; font-size: 9px; font-weight: 600; color: var(--muted); }
          .bcs-cell b { display: block; font-size: 11px; font-weight: 800; color: var(--ink); font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; }
          .bcs-item-foot { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-top: 7px; }
          .bcs-qty {
            display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; font-size: 10px; font-weight: 600;
            color: var(--muted); background: var(--tint); border: 1px solid var(--line); border-radius: 999px;
          }
          .bcs-qty strong { font-size: 12px; font-weight: 800; color: var(--ink); }
          .bcs-qty.editable { color: var(--navy-2); cursor: pointer; }
          .bcs-amt {
            display: flex; align-items: baseline; gap: 5px; padding: 3px 11px; border-radius: 999px; color: #fff;
            background: linear-gradient(135deg, var(--rose), var(--rose-deep));
          }
          .bcs-amt span { font-size: 9px; font-weight: 600; opacity: 0.85; }
          .bcs-amt strong { font-size: 13px; font-weight: 800; font-variant-numeric: tabular-nums; }

          /* ---- bottom bar ---- */
          .bcs-bar {
            position: fixed; left: 50%; bottom: 0; transform: translateX(-50%); width: 100%; max-width: 560px;
            z-index: 20; padding: 9px 12px calc(9px + env(safe-area-inset-bottom, 0px));
            background: rgba(255,255,255,0.96); backdrop-filter: blur(10px);
            border-radius: 18px 18px 0 0; border-top: 1px solid var(--line);
            box-shadow: 0 -10px 24px -14px rgba(18,28,77,0.5);
          }
          .bcs-bar-top { display: flex; align-items: flex-end; justify-content: space-between; gap: 8px; }
          .bcs-net-label { font-size: 10px; font-weight: 700; color: var(--muted); }
          .bcs-net { font-size: 22px; font-weight: 800; line-height: 1.1; color: var(--rose-deep); font-variant-numeric: tabular-nums; }
          .bcs-qtybadge {
            padding: 3px 12px; font-size: 11px; font-weight: 700; color: var(--rose-deep);
            background: var(--rose-soft); border: 1px solid #f8d2d9; border-radius: 999px;
          }
          .bcs-qtybadge b { font-size: 12.5px; font-weight: 800; margin-left: 2px; }
          .bcs-chips { display: flex; gap: 6px; margin-top: 7px; }
          .bcs-chip { flex: 1; padding: 3px 10px; font-size: 9.5px; font-weight: 700; color: var(--muted); background: var(--tint); border-radius: 9px; }
          .bcs-chip b { display: block; font-size: 11.5px; font-weight: 800; color: var(--ink); font-variant-numeric: tabular-nums; }
          .bcs-chip.due b { color: var(--rose-deep); }
          .bcs-bar-actions { display: flex; align-items: center; gap: 7px; margin-top: 8px; }
          .bcs .bcs-save.ant-btn, .bcs .bcs-print.ant-btn {
            flex: 1; height: 38px; border: 0; border-radius: 12px; font-size: 12px; font-weight: 800;
            letter-spacing: 0.4px; color: #fff; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          }
          .bcs .bcs-save.ant-btn { background: linear-gradient(135deg, var(--mint), var(--mint-deep)); box-shadow: 0 7px 14px -10px rgba(34,154,121,0.95); }
          .bcs .bcs-print.ant-btn { background: linear-gradient(135deg, var(--gold), var(--gold-deep)); box-shadow: 0 7px 14px -10px rgba(200,145,43,0.95); }
          .bcs .bcs-save.ant-btn:disabled, .bcs .bcs-print.ant-btn:disabled { opacity: 0.7; color: #fff; }
          .bcs-sq {
            width: 38px; height: 38px; flex-shrink: 0; display: grid; place-items: center; padding: 0; cursor: pointer;
            border-radius: 12px; color: var(--navy); background: var(--tint); border: 1px solid var(--line);
            transition: transform .15s ease;
          }
          .bcs-sq:active { transform: scale(0.92); }
          .bcs-sq.qr { color: #fff; border: 0; background: linear-gradient(135deg, var(--navy), var(--navy-2)); }

          /* ---- bottom sheets ---- */
          .bcs-sheet-wrap {
            position: fixed; inset: 0; z-index: 1000; display: flex; align-items: flex-end; justify-content: center;
            background: rgba(18,28,77,0.45);
          }
          .bcs-sheet {
            width: 100%; max-width: 560px; max-height: 86vh; overflow-y: auto;
            padding: 8px 14px calc(14px + env(safe-area-inset-bottom, 0px));
            background: var(--card); border-radius: 20px 20px 0 0;
            box-shadow: 0 -12px 28px -14px rgba(18,28,77,0.6);
            font-family: "Manrope", "Segoe UI", system-ui, sans-serif; color: var(--ink);
          }
          .bcs-grab { width: 38px; height: 4px; margin: 0 auto 8px; border-radius: 999px; background: var(--line); }
          .bcs-sheet-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
          .bcs-sheet-title { font-size: 16px; font-weight: 800; color: var(--navy); }
          .bcs-sheet-sub { margin: 0 0 12px; font-size: 11px; font-weight: 600; color: var(--muted); line-height: 1.45; }
          .bcs-sheet-x {
            width: 28px; height: 28px; display: grid; place-items: center; padding: 0; cursor: pointer;
            border: 0; border-radius: 9px; color: var(--muted); background: var(--tint);
          }

          /* entry dialogs */
          .bcs-bigfield {
            display: flex; align-items: center; gap: 10px; padding: 0 14px; height: 58px;
            border-radius: 16px; background: var(--tint); border: 1.5px solid var(--line); color: var(--navy-2);
          }
          .bcs-bigfield:focus-within { background: #fff; border-color: var(--navy-2); box-shadow: 0 0 0 4px rgba(29,47,122,0.1); }
          .bcs-bigin {
            flex: 1; min-width: 0; height: 100%; border: 0; outline: none; background: transparent;
            font-size: 22px; font-weight: 800; letter-spacing: 2px; color: var(--ink); text-transform: uppercase;
          }
          .bcs-bigin::placeholder { font-size: 13px; letter-spacing: 0.4px; font-weight: 600; color: #a6aec6; text-transform: none; }
          .bcs-status {
            display: flex; align-items: center; gap: 8px; margin-top: 10px; padding: 8px 12px;
            font-size: 12px; font-weight: 700; border-radius: 12px;
          }
          .bcs-status.ok { color: #0d5c43; background: var(--mint-soft); }
          .bcs-status.err { color: var(--rose-deep); background: var(--rose-soft); }
          .bcs-status.warn { color: #8a5a0c; background: #fbf1da; }
          .bcs-primary {
            width: 100%; height: 46px; margin-top: 12px; border: 0; border-radius: 14px; cursor: pointer;
            font-size: 14px; font-weight: 800; color: #fff; letter-spacing: 0.3px;
            background: linear-gradient(135deg, var(--navy), var(--navy-2));
            box-shadow: 0 10px 18px -12px rgba(18,28,77,0.9);
          }
          .bcs-primary:disabled { opacity: 0.6; }
          .bcs-primary.gold { background: linear-gradient(135deg, var(--gold), var(--gold-deep)); box-shadow: 0 10px 18px -12px rgba(200,145,43,0.9); }
          .bcs-ghost {
            width: 100%; height: 40px; margin-top: 8px; border-radius: 12px; cursor: pointer;
            font-size: 12.5px; font-weight: 800; color: var(--navy-2); background: var(--tint); border: 1px solid var(--line);
          }
          .bcs-ghost.danger { color: var(--rose); background: var(--rose-soft); border-color: #f8d2d9; }
          .bcs-recent-title { margin: 14px 2px 6px; font-size: 10.5px; font-weight: 800; color: var(--muted); }
          .bcs-recent { display: flex; flex-direction: column; gap: 6px; }
          .bcs-recent-row {
            display: flex; align-items: center; gap: 8px; padding: 6px 10px; border-radius: 12px;
            background: var(--tint); font-size: 11px; font-weight: 600; color: var(--muted);
          }
          .bcs-recent-row b { padding: 2px 8px; font-size: 11px; font-weight: 800; color: #0d5c43; background: var(--mint-soft); border-radius: 8px; }
          .bcs-recent-row span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .bcs-sm-card {
            display: flex; align-items: center; gap: 10px; margin-bottom: 12px; padding: 10px 12px;
            border-radius: 14px; background: var(--mint-soft); color: #0d5c43;
          }
          .bcs-sm-card b { display: block; font-size: 13px; font-weight: 800; }
          .bcs-sm-card small { font-size: 10.5px; font-weight: 600; opacity: 0.8; }

          /* print options */
          .bcs-opt {
            display: flex; align-items: center; gap: 12px; width: 100%; margin-bottom: 8px; padding: 11px 12px;
            text-align: left; cursor: pointer; color: var(--ink);
            border-radius: 14px; border: 1px solid var(--line); background: var(--card);
          }
          .bcs-opt:active { background: var(--tint); }
          .bcs-opt-ico {
            width: 38px; height: 38px; flex-shrink: 0; display: grid; place-items: center; border-radius: 12px; color: #fff;
          }
          .bcs-opt-ico.navy { background: linear-gradient(135deg, var(--navy), var(--navy-2)); }
          .bcs-opt-ico.gold { background: linear-gradient(135deg, var(--gold), var(--gold-deep)); }
          .bcs-opt-txt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
          .bcs-opt-txt b { font-size: 13px; font-weight: 800; }
          .bcs-opt-txt small { font-size: 10.5px; font-weight: 600; color: var(--muted); line-height: 1.3; }

          /* bills + customer list */
          .bcs-sheet.bills { display: flex; flex-direction: column; overflow: hidden; }
          .bcs-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; padding: 3px; border-radius: 12px; background: var(--tint); }
          .bcs-tab {
            height: 32px; border: 0; border-radius: 10px; cursor: pointer; font-size: 11.5px; font-weight: 800;
            color: var(--muted); background: transparent;
          }
          .bcs-tab.on { color: #fff; background: linear-gradient(135deg, var(--navy), var(--navy-2)); }
          .bcs-tab.on.ret { background: linear-gradient(135deg, var(--rose), var(--rose-deep)); }
          .bcs-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: 8px; }
          .bcs-dates label { display: flex; flex-direction: column; gap: 2px; font-size: 9.5px; font-weight: 700; color: var(--muted); }
          .bcs-dates input {
            height: 34px; padding: 0 8px; border-radius: 10px; font-size: 12px; font-weight: 700;
            color: var(--ink); background: var(--tint); border: 1px solid var(--line); outline: none;
          }
          .bcs-quick { display: flex; gap: 6px; margin-top: 6px; }
          .bcs-quick button {
            flex: 1; height: 28px; border-radius: 999px; cursor: pointer; font-size: 10.5px; font-weight: 800;
            color: var(--navy-2); background: #e9edfb; border: 0;
          }
          .bcs-sheet-search { display: grid; grid-template-columns: 1fr 64px; gap: 6px; margin: 8px 0; }
          .bcs-sheet-search input {
            height: 36px; padding: 0 11px; border-radius: 10px; font-size: 14px; font-weight: 700;
            color: var(--ink); background: var(--tint); border: 1px solid var(--line); outline: none;
          }
          .bcs-sheet-search input:focus { background: #fff; border-color: var(--navy-2); box-shadow: 0 0 0 3px rgba(29,47,122,0.1); }
          .bcs-go {
            border: 0; border-radius: 10px; cursor: pointer; font-size: 12px; font-weight: 800; color: #fff;
            background: linear-gradient(135deg, var(--mint), var(--mint-deep));
          }
          .bcs-go:disabled { opacity: 0.5; }
          .bcs-rows { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; }
          .bcs-row {
            display: flex; align-items: center; gap: 9px; width: 100%; margin-bottom: 6px; padding: 8px 10px;
            text-align: left; cursor: pointer; border-radius: 12px; border: 1px solid var(--line); background: var(--card);
            color: inherit;
          }
          .bcs-row:active { background: var(--tint); }
          .bcs-row-no {
            min-width: 44px; padding: 4px 8px; text-align: center; font-size: 12px; font-weight: 800;
            color: #fff; border-radius: 9px; background: linear-gradient(135deg, var(--navy), var(--navy-2));
          }
          .bcs-row-no.ret { background: linear-gradient(135deg, var(--rose), var(--rose-deep)); }
          .bcs-row-mid { flex: 1; min-width: 0; }
          .bcs-row-name { font-size: 11.5px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .bcs-row-sub { font-size: 10px; font-weight: 600; color: var(--muted); }
          .bcs-row-amt { font-size: 12px; font-weight: 800; font-variant-numeric: tabular-nums; }
          .bcs-note { padding: 18px 8px; text-align: center; font-size: 11px; font-weight: 600; color: var(--muted); }

          @media (max-width: 340px) { .bcs-grid { grid-template-columns: repeat(2, 1fr); } }
          @media (min-width: 700px) { .bcs { max-width: 560px; margin: 0 auto; } }

                    /* ---- entry card (design code + SM code) ---- */
          .bcs-entry {
            margin-top: 8px; padding: 10px; border-radius: 16px;
            background: var(--card); border: 1px solid var(--line);
            box-shadow: 0 6px 16px -14px rgba(18, 28, 77, 0.5);
          }
          .bcs-entry-label { display: flex; align-items: center; gap: 7px; margin-bottom: 6px; font-size: 11.5px; font-weight: 800; color: var(--navy); }
          .bcs-entry-label .lico { width: 22px; height: 22px; display: grid; place-items: center; border-radius: 7px; }
          .bcs-entry-label .lico.design { color: #fff; background: linear-gradient(135deg, var(--navy), var(--navy-2)); }
          .bcs-entry-label .lico.sm { color: var(--gold-deep); background: #fbf1da; }
          .bcs-entry-count { margin-left: auto; padding: 2px 9px; font-size: 10px; font-weight: 800; color: var(--mint-deep); background: var(--mint-soft); border-radius: 999px; }
          .bcs-entry-row { display: flex; gap: 7px; }
          .bcs-fin {
            flex: 1; min-width: 0; height: 42px; padding: 0 12px; border-radius: 12px; outline: none;
            border: 1.5px solid var(--line); background: var(--tint);
            font-size: 16px; font-weight: 800; letter-spacing: 1.5px; color: var(--ink); text-transform: uppercase;
          }
          .bcs-fin::placeholder { font-size: 12px; letter-spacing: 0.3px; font-weight: 600; color: #a6aec6; text-transform: none; }
          .bcs-fin:focus { background: #fff; border-color: var(--navy-2); box-shadow: 0 0 0 3px rgba(29,47,122,0.1); }
          .bcs-add {
            min-width: 74px; height: 42px; border: 0; border-radius: 12px; cursor: pointer;
            font-size: 12.5px; font-weight: 800; color: #fff;
            background: linear-gradient(135deg, var(--navy), var(--navy-2));
            box-shadow: 0 8px 14px -10px rgba(18,28,77,0.9);
          }
          .bcs-add.gold { background: linear-gradient(135deg, var(--gold), var(--gold-deep)); box-shadow: 0 8px 14px -10px rgba(200,145,43,0.9); }
          .bcs-add:disabled { opacity: 0.6; }
          .bcs-divider { height: 1px; margin: 10px 0; background: var(--line); }
          .bcs-smchip {
            display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 12px;
            background: var(--mint-soft); color: #0d5c43; font-size: 12px; font-weight: 800;
          }
          .bcs-smchip span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .bcs-smchip button {
            width: 26px; height: 26px; display: grid; place-items: center; padding: 0; cursor: pointer;
            border: 0; border-radius: 8px; color: var(--rose); background: #fff;
          }
        `}</style>

                <div ref={qrRef} style={{ display: "none" }}>
                    <QRCodeCanvas value={upiLink || "upi"} size={200} includeMargin={true} />
                </div>

                {/* ---------- Return hero ---------- */}
                <div className="bcs-hero">
                    <div className="bcs-hero-main">
                        <span className="bcs-hero-label">{invNo ? "Editing return" : "New sale return"}</span>
                        <div className="bcs-hero-row">
                            <span className="bcs-hero-no">#{invNo ? invNo : returnNo}</span>
                            <span className="bcs-hero-date">
                                {dayjs(billDate || undefined).format("DD-MMM-YYYY")}
                            </span>
                        </div>
                    </div>
                    <div className="bcs-hero-actions">
                        <button className="bcs-ico mint" onClick={() => setSheetOpen(true)} aria-label="Find a bill">
                            <Ico name="list" size={16} />
                        </button>
                        <button className="bcs-ico" onClick={handleResetClick} aria-label="Start over">
                            <Ico name="refresh" size={16} />
                        </button>
                        <button className="bcs-ico gold" onClick={openCreateCustomer} aria-label="New customer">
                            <Ico name="userPlus" size={16} />
                        </button>
                    </div>
                </div>

                {/* ---------- Customer (browse / edit / remove) ---------- */}
                <div className="bcs-cust">
                    <button className="bcs-cust-main" onClick={openCustList}>
                        <span className={`bcs-avatar${hasCustomer ? "" : " empty"}`}>
                            {hasCustomer ? custInitial : <Ico name="user" size={15} />}
                        </span>
                        <span className="bcs-cust-txt">
                            <b>{hasCustomer ? userDetails?.Custname : "Select customer"}</b>
                            <small>
                                {hasCustomer
                                    ? `${userDetails?.Mobileno ?? ""}${userDetails?.Area && userDetails.Area !== "-" ? " · " + userDetails.Area : ""}`
                                    : "Tap to choose · needed for credit returns"}
                            </small>
                        </span>
                        <Ico name="chevron" size={14} />
                    </button>
                    {hasCustomer && (
                        <>
                            <button className="bcs-cust-act edit" aria-label="Edit customer" onClick={openEditCustomer}>
                                <Ico name="edit" size={16} />
                            </button>
                            <button className="bcs-cust-act" aria-label="Remove customer" onClick={handleRemoveCustomer}>
                                <Ico name="close" size={16} />
                            </button>
                        </>
                    )}
                </div>

                <div className="bcs-entry">
                    <div className="bcs-entry-label">
                        <span className="lico sm"><Ico name="idcard" size={13} /></span>
                        SM code
                    </div>
                    {appliedSm ? (
                        <div className="bcs-smchip">
                            <Ico name="check" size={15} />
                            <span>{appliedSm.CODE}{appliedSm.NAME ? " · " + appliedSm.NAME : ""}</span>
                            <button onClick={clearSmCode} aria-label="Remove SM code">
                                <Ico name="close" size={13} />
                            </button>
                        </div>
                    ) : (
                        <div className="bcs-entry-row">
                            <input
                                ref={smInputRef}
                                className="bcs-fin"
                                placeholder="Enter SM code (optional)"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="characters"
                                spellCheck={false}
                                enterKeyHint="go"
                                value={smDraft}
                                onChange={(e) => {
                                    setSmDraft(e.target.value.replace(/[^A-Za-z0-9_-]/g, ""));
                                    if (smStatus) setSmStatus(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        smCodeAPI();
                                    }
                                }}
                            />
                            <button className="bcs-add gold" disabled={smBusy} onClick={smCodeAPI}>
                                {smBusy ? "..." : "Set"}
                            </button>
                        </div>
                    )}
                    <div className="bcs-divider" />
                    <div className="bcs-entry-label">
                        <span className="lico design"><Ico name="barcode" size={13} /></span>
                        Design code
                        {barCodeData.length > 0 && (
                            <span className="bcs-entry-count">
                                {barCodeData.length} {barCodeData.length === 1 ? "item" : "items"}
                            </span>
                        )}
                    </div>
                    <div className="bcs-entry-row">
                        <input
                            ref={tagNoRef}
                            className="bcs-fin"
                            autoFocus
                            placeholder="Type or scan design code"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            enterKeyHint="go"
                            value={designInput}
                            onChange={(e) => {
                                setDesignInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""));
                                if (entryStatus) setEntryStatus(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSubmitCode();
                                }
                            }}
                        />
                        <button className="bcs-add" disabled={entryBusy} onClick={handleSubmitCode}>
                            {entryBusy ? "..." : "Add"}
                        </button>
                    </div>
                    {entryStatus ? (
                        <div className={`bcs-status ${entryStatus.type}`}>
                            {entryStatus.type === "ok" ? <Ico name="check" size={15} /> : null}
                            {entryStatus.text}
                        </div>
                    ) : null}

                    {smStatus ? <div className={`bcs-status ${smStatus.type}`}>{smStatus.text}</div> : null}
                </div>

                {/* ---------- Items ---------- */}
                <div className="bcs-list-head">
                    <span className="bcs-list-title">Return items</span>
                    {barCodeData.length > 0 && <span className="bcs-count">{barCodeData.length}</span>}
                </div>

                {barCodeData.length === 0 ? (
                    <div className="bcs-empty">
                        <img src={noData} alt="No Data" />
                        <p>Enter a design code above, or open a saved bill with the list button</p>                    </div>
                ) : (
                    barCodeData.map((code, index) => (
                        <div
                            key={`${code.DesignCode}-${index}`}
                            ref={index === barCodeData.length - 1 ? lastItemRef : null}
                            className="bcs-item"
                        >
                            <div className="bcs-item-top">
                                <div className="bcs-item-left">
                                    <span className="bcs-idx">#{index + 1}</span>
                                    <span className="bcs-code">{code.DesignCode}</span>
                                    {isValidValue(code.SMCODE) ? <span className="bcs-sm">SM {code.SMCODE}</span> : null}
                                </div>
                                <div className="bcs-item-right">
                                    <button className="bcs-mini tax" onClick={() => handleCardOpen(code)} aria-label="Tax details">
                                        <Ico name="receipt" size={14} />
                                    </button>
                                    {canEdit && (
                                        <button
                                            className="bcs-mini"
                                            onClick={() => handleDelete(index)}
                                            aria-label="Delete item"
                                        >
                                            <Ico name="trash" size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="bcs-name">{code.ProdName}</div>

                            <div className="bcs-grid">
                                <div className="bcs-cell">
                                    <span>Rate</span>
                                    <b>{Number(code.CPrice).toFixed(2)}</b>
                                </div>
                                <div className="bcs-cell">
                                    <span>Dis %</span>
                                    <b>{Number(code.Disper) + "%"}</b>
                                </div>
                                <div className="bcs-cell">
                                    <span>Dis amt</span>
                                    <b>{Number(code.Disamt).toFixed(2)}</b>
                                </div>
                                <div className="bcs-cell">
                                    <span>Act rate</span>
                                    <b>{Number(code.offer).toFixed(2)}</b>
                                </div>
                            </div>

                            <div className="bcs-item-foot">
                                <div
                                    className={`bcs-qty${canEdit ? " editable" : ""}`}
                                    onClick={() => {
                                        if (canEdit) {
                                            setItemDetailsOpen(true);
                                            setItemDetailsData(code);
                                            setItemDEsignCode(index);
                                        }
                                    }}
                                >
                                    Qty <strong>{code.stock}</strong>
                                    {canEdit && <Ico name="edit" size={11} />}
                                </div>
                                <div className="bcs-amt">
                                    <span>Amt</span>
                                    <strong>{Number(code.stock * code.offer).toFixed(2)}</strong>
                                </div>
                            </div>
                        </div>
                    ))
                )}

                {/* ---------- Bottom checkout bar ---------- */}
                <div className="bcs-bar">
                    <div className="bcs-bar-top">
                        <div>
                            <div className="bcs-net-label">Return amount</div>
                            <div className="bcs-net">{"₹ " + Number(totalNetAmt).toFixed(2)}</div>
                        </div>
                        <span className="bcs-qtybadge">
                            Qty <b>{totalQty}</b>
                        </span>
                    </div>

                    <div className="bcs-chips">
                        <div className="bcs-chip">
                            Gross
                            <b>{"₹ " + Number(totalGrossAmt).toFixed(2)}</b>
                        </div>
                        <div className="bcs-chip">
                            Discount
                            <b>{"₹ " + (Number(totalDisAmt) + Number(billDiscAmt)).toFixed(2)}</b>
                        </div>
                        {creditAmt > 0 ? (
                            <div className="bcs-chip due">
                                Credit due
                                <b>{"₹ " + Number(creditAmt).toFixed(0)}</b>
                            </div>
                        ) : hasCustomer ? (
                            <div className="bcs-chip">
                                Prv. balance
                                <b>{"₹ " + Number(totalBalance).toFixed(0)}</b>
                            </div>
                        ) : null}
                    </div>

                    <div className="bcs-bar-actions">
                        <Button
                            disabled={saving}
                            loading={saving}
                            className="bcs-save"
                            icon={<Ico name="save" size={15} />}
                            onClick={handleSave}
                        >
                            {invNo ? "UPDATE" : "SAVE"}
                        </Button>
                        <Button
                            disabled={printSaving}
                            loading={printSaving}
                            className="bcs-print"
                            icon={<Ico name="printer" size={15} />}
                            onClick={handlePrintClick}
                        >
                            PRINT
                        </Button>
                        {barCodeData.length > 0 && (
                            <button className="bcs-sq" onClick={handleCashOpen} aria-label="Payment mode">
                                <Ico name="wallet" size={18} />
                            </button>
                        )}
                        {barCodeData.length > 0 && upiAmt > 0 && (
                            <button className="bcs-sq qr" onClick={handleQrcodeOpen} aria-label="UPI QR code">
                                <Ico name="qr" size={18} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ---------- Sheet: browse customers ---------- */}
            <BottomSheet
                open={custListOpen}
                title="Select customer"
                sub="Search by name or mobile number, or add a new customer."
                onClose={closeCustList}
                className="bills"
            >
                <div className="bcs-sheet-search">
                    <input
                        autoFocus
                        placeholder="Name or mobile"
                        value={custSearch}
                        onChange={(e) => setCustSearch(e.target.value)}
                    />
                    <button className="bcs-go" onClick={openCreateCustomer}>+ New</button>
                </div>
                <div className="bcs-rows">
                    {custListLoading ? (
                        <div className="bcs-note">Loading customers...</div>
                    ) : visibleCustomers.length === 0 ? (
                        <div className="bcs-note">No customer found. Tap "+ New" to add one.</div>
                    ) : (
                        visibleCustomers.map((c, i) => (
                            <button key={`${c.Mobileno}-${i}`} className="bcs-row" onClick={() => pickCustomer(c)}>
                                <span className="bcs-avatar">{(c.Custname || "?").trim()[0]?.toUpperCase()}</span>
                                <div className="bcs-row-mid">
                                    <div className="bcs-row-name">{c.Custname}</div>
                                    <div className="bcs-row-sub">
                                        {[c.Mobileno, c.Area && c.Area !== "-" ? c.Area : ""].filter(Boolean).join(" · ")}
                                    </div>
                                </div>
                                <Ico name="chevron" size={14} />
                            </button>
                        ))
                    )}
                </div>
            </BottomSheet>

            {/* ---------- Bills sheet ---------- */}
            <BottomSheet
                open={sheetOpen}
                title="Find a bill"
                sub={
                    sheetTab === "RETURN"
                        ? "Open a saved return to review or modify it."
                        : "Start a new return from an original sale."
                }
                onClose={closeSheet}
                className="bills"
            >
                <div className="bcs-tabs">
                    <button
                        className={`bcs-tab ret${sheetTab === "RETURN" ? " on" : ""}`}
                        onClick={() => setSheetTab("RETURN")}
                    >
                        Return bills
                    </button>
                    <button
                        className={`bcs-tab${sheetTab === "SALE" ? " on" : ""}`}
                        onClick={() => setSheetTab("SALE")}
                    >
                        Sale bills
                    </button>
                </div>

                <div className="bcs-dates">
                    <label>
                        From
                        <input
                            type="date"
                            value={sheetFrom}
                            max={sheetTo || undefined}
                            onChange={(e) => setSheetFrom(e.target.value)}
                        />
                    </label>
                    <label>
                        To
                        <input
                            type="date"
                            value={sheetTo}
                            min={sheetFrom || undefined}
                            max={dayjs().format("YYYY-MM-DD")}
                            onChange={(e) => setSheetTo(e.target.value)}
                        />
                    </label>
                </div>
                <div className="bcs-quick">
                    <button onClick={() => setRange(0)}>Today</button>
                    <button onClick={() => setRange(6)}>7 days</button>
                    <button onClick={() => setRange(29)}>30 days</button>
                </div>

                <div className="bcs-sheet-search">
                    <input
                        inputMode="numeric"
                        placeholder={sheetTab === "RETURN" ? "Return bill no" : "Sale bill no"}
                        value={sheetSearch}
                        onChange={(e) => setSheetSearch(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") pickBill(Number(sheetSearch));
                        }}
                    />
                    <button className="bcs-go" disabled={!sheetSearch} onClick={() => pickBill(Number(sheetSearch))}>
                        Open
                    </button>
                </div>

                <div className="bcs-rows">
                    {sheetLoading ? (
                        <div className="bcs-note">Loading bills...</div>
                    ) : visibleRows.length === 0 ? (
                        <div className="bcs-note">
                            {sheetSearch
                                ? "Not in this date range. Tap Open to look it up directly."
                                : "No bills in this date range."}
                        </div>
                    ) : (
                        visibleRows.map((r, i) => {
                            const no = pick(r, "billno", "BILLNO", "BillNo");
                            const name = pick(r, "CustName", "custName", "CUSTNAME") || "Walk-in";
                            const phone = pick(r, "PhoneNo", "phoneNo", "PHONENO");
                            const qtyRow = pick(r, "totqty", "TOTQTY");
                            const d = pick(r, "billdate", "BillDate", "BILLDATE");
                            const amt = pick(r, "NETAMT", "netamt", "NetAmt");
                            const sub = [
                                d && dayjs(d).isValid() ? dayjs(d).format("DD-MMM-YYYY") : "",
                                phone && phone !== "-" ? phone : "",
                                qtyRow !== "" ? `Qty ${qtyRow}` : "",
                            ]
                                .filter(Boolean)
                                .join(" · ");
                            return (
                                <button key={`${no}-${i}`} className="bcs-row" onClick={() => pickBill(Number(no))}>
                                    <span className={`bcs-row-no${sheetTab === "RETURN" ? " ret" : ""}`}>#{no}</span>
                                    <div className="bcs-row-mid">
                                        <div className="bcs-row-name">{name}</div>
                                        <div className="bcs-row-sub">{sub}</div>
                                    </div>
                                    {amt !== "" ? (
                                        <span className="bcs-row-amt">{"₹ " + Number(amt).toFixed(2)}</span>
                                    ) : null}
                                </button>
                            );
                        })
                    )}
                </div>
            </BottomSheet>

            {/* ---------- Sheet: how to print ---------- */}
            <BottomSheet
                open={printOpen}
                title="Save and print"
                sub="The return is saved first. Printing starts only after it saves."
                onClose={() => setPrintOpen(false)}
            >
                <button className="bcs-opt" onClick={() => handlePrintSave("PDF")}>
                    <span className="bcs-opt-ico navy"><Ico name="receipt" size={18} /></span>
                    <span className="bcs-opt-txt">
                        <b>Download PDF</b>
                        <small>Sale return receipt, 90 mm wide</small>
                    </span>
                    <Ico name="chevron" size={14} />
                </button>
                <button className="bcs-opt" onClick={() => handlePrintSave("EPSON")}>
                    <span className="bcs-opt-ico gold"><Ico name="printer" size={18} /></span>
                    <span className="bcs-opt-txt">
                        <b>Thermal printer (Epson)</b>
                        <small>Prints over your shop Wi-Fi to the saved printer IP</small>
                    </span>
                    <Ico name="chevron" size={14} />
                </button>
            </BottomSheet>

            {/* ---------- Dialogs (your mobile components) ---------- */}
            <CustomerDialog
                open={custOpen}
                onCancel={closeCustDialog}
                onSave={createCustomerDetailsAPI}
                mode={custMode}
                lockName={custMode === "edit" && Number(prvBalance) !== 0}
                formValues={formValues}
                setFormValues={setFormValues}
                phoneNo={phoneNo}
                setPhoneNo={setPhoneNo}
                userDetailsAPI={userDetailsAPI}
                phone1Ref={mobileRef}
                searchRef={searchRef}
                handleMobileNoKeyDown={handleMobileNoKeyDown}
            />
            <SingleCardDetails
                cardOpen={cardOpen}
                setCardOpen={setCardOpen}
                handleOk={handleCardCancel}
                handleCancel={handleCardCancel}
                titleValue={titleValue}
                gst={gst}
                gstAmt={gstAmt}
                cGstRate={cGstRate}
                cGstAmt={cGstAmt}
                sGstRate={sGstRate}
                sGstAmt={sGstAmt}
                iGstRate={iGstRate}
                iGstAmt={iGstAmt}
                basicAmt={basicAmt}
                netAmt={netAmt}
                smCode={smCodeView}
            />
            <CashMethod
                cashOpen={cashOpen}
                setCashOpen={setCashOpen}
                handleOk={handleCashOpen}
                handleCancel={handleCashCancel}
                cashValue={cashValue}
                setCashValue={setCashValue}
                totalNetAmt={totalNetAmt}
                cashAmt={cashAmt}
                setCashAmt={setCashAmt}
                cardAmt={cardAmt}
                setCardAmt={setCardAmt}
                upiAmt={upiAmt}
                setUpiAmt={setUpiAmt}
                onlineAmt={onlineAmt}
                setOnlineAmt={setOnlineAmt}
                creditAmt={creditAmt}
                setCreditAmt={setCreditAmt}
                setSwitchCredit={setSwitchCredit}
                switchCredit={switchCredit}
                creditCashValue={creditCashValue}
                setCreditCashValue={setCreditCashValue}
                creditCashAmt={creditCashAmt}
                setCreditCashAmt={setCreditCashAmt}
                creditCardAmt={creditCardAmt}
                setCreditCardAmt={setCreditCardAmt}
                creditUpiAmt={creditUpiAmt}
                setCreditUpiAmt={setCreditUpiAmt}
                creditOnlineAmt={creditOnlineAmt}
                setCreditOnlineAmt={setCreditOnlineAmt}
            />
            <HomeBillDialog
                homeDetailsOpen={homeDetailsOpen}
                setHomeDetailsOpen={setHomeDetailsOpen}
                qty={qty}
                setQty={setQty}
                amount={amount}
                setAmount={setAmount}
                disCount={disCount}
                setDisCount={setDisCount}
                rate={rate}
                setRate={setRate}
                disAmt={disAmt}
                setDisAmt={setDisAmt}
                productName={productName}
                setProductName={setProductName}
                designCode={designCode}
                setDesignCode={setDesignCode}
                setPTypeName={setPTypeName}
                setHsnCode={setHsnCode}
                productData={productData}
                setProductData={setProductData}
                setBrandName={setBrandName}
                handleHomeBillSave={handleHomeBillSave}
            />
            <EditItemDetails
                itemDetailsOpen={itemDetailsOpen}
                setItemDetailsOpen={setItemDetailsOpen}
                setItemDetailsData={setItemDetailsData}
                itemDetailsData={itemDetailsData}
                itemDesignCode={itemDesignCode}
                setBarCodeData={setBarCodeData}
                barCodeData={barCodeData}
                qtyData={qtyData}
                offerARate={0}
                offerBRate={0}
                adminType={adminType}
            />
            <QrCodeDialog
                qrCodeOpen={qrCodeOpen}
                handleQrcodeCancel={handleQrcodeCancel}
                base64QR={base64QR}
            />
        </div>
    );
};

export default SaleReturnScanner;